/**
 * POLYGON API Client
 * Connects to the POLYGON plume prediction and cost analysis API
 */

import { apiClient } from './client';

// ============================================================================
// TYPES - Match backend PolygonOutput structure
// ============================================================================

export interface SiteInput {
  latitude: number;
  longitude: number;
  site_name: string;
  contaminant_type: string;
  plume_area_sqft?: number;
  plume_length_ft?: number;
  plume_width_ft?: number;
  max_concentration_ug_l?: number;
  aquifer_k_ft_day?: number;
  aquifer_porosity?: number;
  hydraulic_gradient?: number;
  flow_direction_degrees?: number;
  monitoring_wells?: MonitoringWellInput[];
}

export interface MonitoringWellInput {
  well_id: string;
  latitude: number;
  longitude: number;
  distance_from_source_ft: number;
  direction_from_source_degrees: number;
  install_date?: string;
}

export interface PolygonAnalysisRequest {
  site_data: SiteInput;
  projection_months?: number;
  auto_detect_wells?: boolean;
  well_search_radius_ft?: number;
}

export interface PeriodPrediction {
  period_months: number;
  predicted_area_sqft: number;
  predicted_length_ft: number;
  predicted_width_ft: number;
  predicted_max_conc_ug_l: number;
  wells_breached: string[];
  new_wells_needed: number;
  cumulative_wells_installed: number;
  period_monitoring_cost: number;
  period_well_install_cost: number;
  cumulative_cost: number;
  risk_level: string;
  confidence: number;
}

export interface ExistingWell {
  well_id: string;
  source_database?: string;
  source?: string;
  latitude: number;
  longitude: number;
  distance_ft: number;
  well_depth_ft: number | null;
  depth_ft?: number | null;  // Alternative field from some API endpoints
  well_type: string | null;
  status: string | null;
}

export interface RecommendedWell {
  well_id: string;
  latitude: number;
  longitude: number;
  purpose: string;
  recommended_depth_ft: number;
  estimated_install_cost: number;
  rationale: string;
}

export interface WellSummary {
  existing_count: number;
  recommended_count: number;
  total_recommended_cost: number;
}

export interface PolygonOutput {
  site_name: string;
  site_id: string;
  analysis_timestamp: string;

  // Current state
  current_plume: {
    area_sqft: number;
    length_ft: number;
    width_ft: number;
    max_concentration_ug_l: number;
  };

  // Toolkit data
  toolkit_data: {
    soil_type: string;
    aquifer_k_ft_day: number;
    aquifer_porosity: number;
    depth_to_water_ft: number;
    land_use: string;
    nearby_receptors: string[];
  };

  // Period predictions
  predictions: PeriodPrediction[];

  // Existing wells found
  existing_wells: ExistingWell[];

  // Summary
  total_projection_months: number;
  total_estimated_cost: number;
  total_wells_to_install: number;

  // Risk assessment
  threat_tier: 1 | 2 | 3 | 4 | 5;
  risk_summary: string;

  // Physics data
  physics?: {
    darcy_velocity_ft_day: number;
    seepage_velocity_ft_day: number;
    annual_migration_ft: number;
    flow_direction_degrees: number;
  };
}

export interface PortfolioSite {
  site_id: string;
  site_name: string;
  state: string;
  latitude: number;
  longitude: number;
  threat_tier: 1 | 2 | 3 | 4 | 5;
  contaminant_type: string;
  current_cost: number;
  cost_6mo: number;
  cost_12mo: number;
  cost_24mo: number;
  monthly_burn_rate: number;
  plume_area_sqft: number;
  wells_total: number;
  wells_breached: number;
  wells_exceeding_mcl: number;
  sentinel_count: number;
  last_analysis: string;
  status: 'active' | 'monitoring' | 'closed';
}

export interface PaginatedSitesResponse {
  total_count: number;
  page: number;
  page_size: number;
  sites: PortfolioSite[];
}

export interface PortfolioSummary {
  total_sites: number;
  sites_by_tier: Record<1 | 2 | 3 | 4 | 5, number>;
  total_current_cost: number;
  total_cost_6mo: number;
  total_cost_12mo: number;
  total_cost_24mo: number;
  cost_of_waiting_6mo: number;
  monthly_burn_rate: number;
  highest_priority_sites: PortfolioSite[];
}

export interface QuickEstimateRequest {
  latitude: number;
  longitude: number;
  contaminant_type: string;
  plume_area_sqft?: number;
}

// Simulation request/response types
export interface SimulationRequest {
  site_id: string;
  latitude: number;
  longitude: number;
  plume_area_sqft: number;
  max_concentration_ug_L: number;
  contaminant_type?: string;
  source_active?: boolean;
  projection_months?: number;
  hydraulic_conductivity_ft_day?: number;
  porosity?: number;
  hydraulic_gradient?: number;
  flow_direction_degrees?: number;
}

export interface PlumeContourData {
  concentration_ug_L: number;
  area_sqft: number;
  coordinates: [number, number][];
}

export interface PlumeStateData {
  time_months: number;
  date: string;
  plume_area_sqft: number;
  plume_length_ft: number;
  plume_width_ft: number;
  max_concentration_ug_L: number;
  migration_distance_ft: number;
  contours: PlumeContourData[];
  // Centroid coordinates for trajectory visualization
  centroid_lat?: number;
  centroid_lon?: number;
  migration_direction_degrees?: number;
}

export interface PlumeMapContour {
  month: number;
  points: { x: number; y: number }[];
  area: number;
  color: string;
}

export interface SimulationResponse {
  site_id: string;
  simulation_date: string;
  model_type: string;
  confidence_score: number;
  input_parameters: {
    latitude?: number;
    longitude?: number;
    plume_area_sqft?: number;
    max_concentration_ug_L?: number;
    contaminant_type?: string;
    hydraulic_conductivity_ft_day: number;
    porosity: number;
    hydraulic_gradient: number;
    flow_direction_degrees: number;
  };
  summary: {
    peak_plume_area_sqft: number;
    peak_concentration_ug_L?: number;
    total_migration_ft: number;
    num_time_steps?: number;
  };
  plume_states: PlumeStateData[];
  geojson?: Record<string, unknown>;
  plume_map: {
    sourceLocation: { x: number; y: number };
    currentPlume: { x: number; y: number }[];
    predictedContours: PlumeMapContour[];
    gradient?: {
      direction: number;
      magnitude: number;
    };
    bounds?: {
      min_lat: number;
      max_lat: number;
      min_lon: number;
      max_lon: number;
    };
  };
  // Wells data
  existing_wells?: ExistingWell[];
  recommended_wells?: RecommendedWell[];
  well_summary?: WellSummary;
}

export interface QuickEstimateResponse {
  estimated_current_cost: number;
  estimated_cost_6mo: number;
  estimated_cost_12mo: number;
  estimated_cost_24mo: number;
  threat_tier: number;
  confidence: number;
}

export interface SiteDetailsResponse {
  site_id: string;
  site_name: string;
  latitude: number;
  longitude: number;
  contaminant_type: string;
  plume_area_sqft: number;
  max_concentration_ug_L: number;
  // PSH/LNAPL source zone parameters (from database)
  psh_thickness_ft?: number | null;
  psh_area_sqft?: number | null;
  estimated_mass_lbs?: number | null;
  simulation: {
    plume_states: PlumeStateData[];
    model_type: string;
    confidence_score: number;
  } | null;
  hydrogeology: {
    hydraulic_conductivity_ft_day: number;
    porosity: number;
    hydraulic_gradient: number;
    flow_direction_degrees: number;
    aquifer_name: string;
    ground_elevation_ft?: number;
    depth_to_water_ft?: number;
    groundwater_velocity_ft_day?: number;
    // Extended USGS toolkit data (SSURGO, NWIS, 3DEP, TWDB)
    soil_type?: string;
    land_use?: string;
    clay_pct?: number;
    sand_pct?: number;
    organic_matter_pct?: number;
    organic_carbon_fraction?: number;
    ksat_in_hr?: number;
    hydrologic_group?: string;
    water_level_date?: string;
    water_elevation_ft?: number;
    aquifer_type?: string;
    aquifer_code?: string;
    data_sources?: string[];
    confidence_score?: number;
  };
  existing_wells?: ExistingWell[];
  recommended_wells?: RecommendedWell[];
  data_sources?: {
    hydrogeology: {
      sources: string[];
      elevation_source: string;
      gradient_source: string;
      soil_source: string;
      aquifer_source: string;
    };
    existing_wells: {
      sources: string[];
      wells_found: number;
      by_source: Record<string, number>;
    };
    fetch_timestamp: string;
  };
  error?: string;
}

// Contaminant input for multi-contaminant support
export interface ContaminantInput {
  type: string;
  concentration_ug_l: number;
  mcl?: number;
}

/**
 * Consolidated page-bundle response — single request replaces 10+ separate API calls.
 * All fields are nullable to allow partial success (one section can fail without blocking others).
 */
export interface PageBundleResponse {
  site_id: string;
  site_name: string;
  site_details: SiteDetailsResponse | null;
  cost_projection: CostProjectionSnapshotResponse | null;
  plume_snapshots: PlumeSnapshotsResponse | null;
  predictive_snapshots: PredictiveSnapshotsResponse | null;
  timeline: TimelineResponse | null;
  psh_wells: PSHWellMeasurement[];
  gradient_map: SiteGradientMap | null;
  bundle_ms: number;
}

// Combined analysis request/response types
export interface CombinedAnalysisRequest {
  latitude: number;
  longitude: number;
  // Multi-contaminant support
  contaminants?: ContaminantInput[];
  // Legacy single contaminant fields (for backwards compatibility)
  contaminant_type?: string;
  initial_concentration_ug_L?: number;
  plume_area_sqft: number;
  projection_months?: number;
  source_active?: boolean;

  // Hydrogeology overrides - if provided, these override USGS toolkit values
  hydraulic_conductivity_ft_day?: number;
  porosity?: number;
  hydraulic_gradient?: number;
  flow_direction_degrees?: number;
  depth_to_water_ft?: number;
  aquifer_name?: string;

  // Site metadata
  site_id?: string;
  site_name?: string;

  // Pass existing wells from form auto-detection (to avoid re-fetching)
  existing_wells?: Array<{
    well_id: string;
    latitude: number;
    longitude: number;
    source_database?: string;
    well_depth_ft?: number;
    distance_ft?: number;
  }>;
}

export interface HydroData {
  hydraulic_conductivity_ft_day: number;
  porosity: number;
  hydraulic_gradient: number;
  flow_direction_degrees: number;
  depth_to_water_ft: number;
  organic_carbon_foc: number;
  bulk_density: number;
  soil_type: string;
  elevation_ft: number;
  aquifer_name?: string;
  groundwater_velocity_ft_day?: number;
  data_sources?: string[];
  // Auto-detected wells from toolkit APIs
  existing_wells?: ExistingWell[];
  wells_summary?: {
    total_wells_found: number;
    wells_with_contamination: number;
    nearest_well_ft: number | null;
    wells_by_source: Record<string, number>;
    error?: string;
  };
}

export interface CombinedAnalysisResponse {
  analysis_id: string;
  analysis_timestamp: string;
  site_location: {
    latitude: number;
    longitude: number;
  };
  contaminant: {
    type: string;
    initial_concentration_ug_L: number;
    mcl_ug_L: number;
    times_mcl: number;
  };
  hydrogeological_data: HydroData;
  // PSH/LNAPL source zone parameters
  psh_thickness_ft?: number | null;
  psh_area_sqft?: number | null;
  estimated_mass_lbs?: number | null;
  psh_data?: {
    thickness_ft?: number;
    area_sqft?: number;
    estimated_mass_lbs?: number;
  };
  physics_results: {
    model_type: string;
    confidence_score: number;
    transport_properties: {
      retardation_factor: number;
      contaminant_velocity_ft_day: number;
      half_life_days: number;
      decay_rate_per_day: number;
    };
    plume_states: PlumeStateData[];
  };
  ml_predictions: {
    model_version: string;
    predictions: Array<{
      period_months: number;
      predicted_area_sqft: number;
      predicted_cost: number;
      well_breach_probability: number;
      confidence: number;
    }>;
    threat_tier: number;
    risk_summary: string;
  };
  existing_wells: ExistingWell[];
  recommended_wells: RecommendedWell[];
  progressive_well_plan: {
    initial_existing_wells: number;
    period_recommendations: Array<{
      period_months: number;
      plume_migration_ft: number;
      coverage_adequate: boolean;
      coverage_gaps: string[];
      new_wells_recommended: RecommendedWell[];
      period_install_cost: number;
    }>;
    cumulative_wells_by_period: Record<number, number>;
    cumulative_cost_by_period: Record<number, number>;
    total_new_wells_recommended: number;
    total_install_cost: number;
    final_coverage_assessment: string;
  };
  cost_summary: {
    current_remediation_cost: number;
    cost_6_months: number;
    cost_12_months: number;
    cost_24_months: number;
    total_well_installation: number;
    monthly_burn_rate: number;
  };
}

export interface WellSearchResult {
  wells: ExistingWell[];
  search_radius_ft: number;
  total_found: number;
}

// ============================================================================
// THREE-PLUME MODEL TYPES (PSH, Dissolved, Vapor)
// ============================================================================

/**
 * Recovery well for dissolved plume capture.
 * Creates a capture zone that intercepts contaminated groundwater.
 */
export interface RecoveryWell {
  id?: string;
  name?: string;         // Optional display name
  x_ft: number;          // Distance from source (+ is downgradient)
  y_ft: number;          // Lateral offset from plume centerline
  pump_rate_gpd: number; // Extraction rate in gallons per day
  start_month: number;   // Month when pumping begins
  enabled?: boolean;     // Whether well is currently active (default true)
  // For map display
  latitude?: number;
  longitude?: number;
  depth_ft?: number;     // Total well depth from surface (from DB total_depth_ft)
}

/**
 * MDPE (Multi-Phase/Dual-Phase Extraction) well for PSH vapor removal.
 * Extracts vapors from the vadose zone to accelerate source depletion.
 */
export interface MDPEWell {
  id?: string;
  name?: string;           // Optional display name
  x_ft: number;            // Position relative to source center
  y_ft: number;            // Lateral offset
  extraction_cfm: number;  // Vapor extraction rate in cubic feet per minute
  start_month: number;     // Month when extraction begins
  enabled?: boolean;       // Whether well is currently active (default true)
  // For map display
  latitude?: number;
  longitude?: number;
  depth_ft?: number;       // Total well depth from surface (from DB total_depth_ft)
}

export interface ThreePlumeRequest {
  // Site location (required for auto-population or manual input)
  latitude?: number;
  longitude?: number;
  site_id?: string;

  // PSH/LNAPL source zone parameters (at least one required)
  psh_thickness_ft?: number;
  psh_area_sqft?: number;
  psh_volume_gallons?: number;
  psh_saturation_percent?: number;

  // Multi-contaminant support
  contaminants?: ContaminantInput[];
  // Legacy single contaminant (for backwards compatibility)
  contaminant_type?: string;

  // Hydrogeological parameters
  hydraulic_conductivity_ft_day?: number;
  porosity?: number;
  hydraulic_gradient?: number;
  depth_to_water_ft?: number;
  aquifer_thickness_ft?: number;
  organic_carbon_foc?: number;

  // Remediation
  remediation_type?: string;

  // Interactive remediation wells
  recovery_wells?: RecoveryWell[];
  mdpe_wells?: MDPEWell[];

  // Simulation parameters
  projection_months?: number;
  simulation_months?: number;  // Alias for projection_months
  max_concentration_ug_L?: number;
}

export interface ThreePlumePSHState {
  mass_lbs: number;
  area_sqft: number;
  thickness_ft: number;
  depletion_rate_lbs_month: number;
}

export interface ThreePlumeDissolvedState {
  area_sqft: number;
  length_ft: number;
  width_ft: number;
  max_conc_ug_l: number;
  mass_lbs: number;
  // Optional: actual polygon coordinates from AGMR extraction (evolved over time)
  // If present, use these instead of generating ellipse from length/width
  polygon?: [number, number][] | null;
  // Pre-computed synthetic concentration wells for IDW rendering
  concentration_wells?: Array<{
    lat: number;
    lon: number;
    concentration: number;
  }> | null;
}

export interface ThreePlumeVaporState {
  area_sqft: number;
  max_conc_ug_m3: number;
  depth_ft: number;
}

export interface ThreePlumeRates {
  dissolution_lbs_month: number;
  volatilization_lbs_month: number;
  biodeg_lbs_month: number;
  remediation_lbs_month: number;
}

export interface ThreePlumeState {
  time_months: number;
  psh: ThreePlumePSHState;
  dissolved: ThreePlumeDissolvedState;
  vapor: ThreePlumeVaporState;
  rates: ThreePlumeRates;
}

// Per-contaminant result summary
export interface ContaminantResult {
  contaminant_type: string;
  mcl_ug_l: number;
  initial_concentration_ug_l: number;
  peak_dissolved_conc_ug_l: number;
  time_to_peak_dissolved_months: number;
  peak_vapor_conc_ug_m3: number;
  time_to_mcl_months: number | null;
  plume_states: ThreePlumeState[];
}

// ============================================================================
// COMPOUND PHYSICOCHEMICAL PARAMETERS (for multi-contaminant plume modeling)
// ============================================================================

/**
 * Physicochemical parameters for individual compounds.
 * Used to calculate compound-specific plume lengths, retardation factors, etc.
 */
export interface CompoundPlumeParameters {
  compound: string;
  molecular_weight: number;
  solubility_mg_l: number;
  log_kow: number;
  log_koc: number;
  koc_l_kg: number;
  henry_constant: number;
  vapor_pressure_mmhg: number;
  half_life_days: number;
  half_life_aerobic_days: number;
  half_life_anaerobic_days: number;
  retardation_factor: number;
  relative_plume_length: number;
  plume_color: string;
}

/**
 * Compound-specific parameters for BTEX and related petroleum compounds.
 * Values sourced from EPA, ASTM, and peer-reviewed literature.
 *
 * Relative plume lengths based on research showing MTBE extends furthest
 * due to low sorption and slow biodegradation, while toluene is shortest
 * due to rapid aerobic biodegradation.
 */
export const COMPOUND_PARAMETERS: CompoundPlumeParameters[] = [
  {
    compound: 'Benzene',
    molecular_weight: 78.11,
    solubility_mg_l: 1780,
    log_kow: 2.13,
    log_koc: 1.58,
    koc_l_kg: 38,
    henry_constant: 0.228,
    vapor_pressure_mmhg: 75.2,
    half_life_days: 365,
    half_life_aerobic_days: 120,
    half_life_anaerobic_days: 730,
    retardation_factor: 1.1,
    relative_plume_length: 1.0,  // Reference compound
    plume_color: '#3b82f6',  // Blue
  },
  {
    compound: 'Toluene',
    molecular_weight: 92.14,
    solubility_mg_l: 526,
    log_kow: 2.69,
    log_koc: 2.13,
    koc_l_kg: 135,
    henry_constant: 0.272,
    vapor_pressure_mmhg: 22,
    half_life_days: 28,
    half_life_aerobic_days: 14,
    half_life_anaerobic_days: 365,
    retardation_factor: 1.4,
    relative_plume_length: 0.4,  // Shortest - rapid biodegradation
    plume_color: '#22c55e',  // Green
  },
  {
    compound: 'Ethylbenzene',
    molecular_weight: 106.17,
    solubility_mg_l: 152,
    log_kow: 3.15,
    log_koc: 2.41,
    koc_l_kg: 257,
    henry_constant: 0.323,
    vapor_pressure_mmhg: 7,
    half_life_days: 228,
    half_life_aerobic_days: 100,
    half_life_anaerobic_days: 730,
    retardation_factor: 1.7,
    relative_plume_length: 0.7,
    plume_color: '#f59e0b',  // Amber
  },
  {
    compound: 'Xylenes',
    molecular_weight: 106.17,
    solubility_mg_l: 178,
    log_kow: 3.12,
    log_koc: 2.38,
    koc_l_kg: 240,
    henry_constant: 0.290,
    vapor_pressure_mmhg: 8,
    half_life_days: 180,
    half_life_aerobic_days: 60,
    half_life_anaerobic_days: 545,
    retardation_factor: 1.6,
    relative_plume_length: 0.6,
    plume_color: '#a855f7',  // Purple
  },
  {
    compound: 'MTBE',
    molecular_weight: 88.15,
    solubility_mg_l: 51000,
    log_kow: 0.94,
    log_koc: 0.55,
    koc_l_kg: 3.5,
    henry_constant: 0.024,
    vapor_pressure_mmhg: 245,
    half_life_days: 1825,
    half_life_aerobic_days: 730,
    half_life_anaerobic_days: 3650,
    retardation_factor: 1.01,
    relative_plume_length: 2.5,  // Longest - very low sorption, slow biodegradation
    plume_color: '#ef4444',  // Red
  },
  {
    compound: 'Naphthalene',
    molecular_weight: 128.17,
    solubility_mg_l: 31,
    log_kow: 3.30,
    log_koc: 2.94,
    koc_l_kg: 871,
    henry_constant: 0.020,
    vapor_pressure_mmhg: 0.085,
    half_life_days: 365,
    half_life_aerobic_days: 180,
    half_life_anaerobic_days: 1095,
    retardation_factor: 2.5,
    relative_plume_length: 0.5,  // High sorption limits mobility
    plume_color: '#14b8a6',  // Teal
  },
  // Aggregate compounds
  {
    compound: 'Total BTEX',
    molecular_weight: 92,
    solubility_mg_l: 500,
    log_kow: 2.73,
    log_koc: 2.0,
    koc_l_kg: 100,
    henry_constant: 0.25,
    vapor_pressure_mmhg: 28,
    half_life_days: 200,
    half_life_aerobic_days: 75,
    half_life_anaerobic_days: 600,
    retardation_factor: 1.3,
    relative_plume_length: 0.8,
    plume_color: '#06b6d4',  // Cyan
  },
  {
    compound: 'Total TPH',
    molecular_weight: 180,
    solubility_mg_l: 50,
    log_kow: 4.0,
    log_koc: 3.5,
    koc_l_kg: 3162,
    henry_constant: 0.1,
    vapor_pressure_mmhg: 5,
    half_life_days: 365,
    half_life_aerobic_days: 180,
    half_life_anaerobic_days: 730,
    retardation_factor: 2.5,
    relative_plume_length: 0.5,
    plume_color: '#6b7280',  // Gray
  },
  // TPH Carbon Ranges
  {
    compound: 'C6-C12',  // Gasoline Range
    molecular_weight: 100,
    solubility_mg_l: 120,
    log_kow: 3.1,
    log_koc: 2.5,
    koc_l_kg: 316,
    henry_constant: 0.5,
    vapor_pressure_mmhg: 40,
    half_life_days: 180,
    half_life_aerobic_days: 90,
    half_life_anaerobic_days: 545,
    retardation_factor: 1.8,
    relative_plume_length: 0.8,
    plume_color: '#f97316',  // Orange
  },
  {
    compound: 'C12-C28',  // Diesel Range
    molecular_weight: 200,
    solubility_mg_l: 5,
    log_kow: 5.0,
    log_koc: 4.5,
    koc_l_kg: 31623,
    henry_constant: 0.01,
    vapor_pressure_mmhg: 0.1,
    half_life_days: 730,
    half_life_aerobic_days: 365,
    half_life_anaerobic_days: 1825,
    retardation_factor: 5.0,
    relative_plume_length: 0.3,
    plume_color: '#854d0e',  // Brown
  },
  {
    compound: 'C28-C35',  // Motor Oil Range
    molecular_weight: 400,
    solubility_mg_l: 0.5,
    log_kow: 7.0,
    log_koc: 6.0,
    koc_l_kg: 1000000,
    henry_constant: 0.001,
    vapor_pressure_mmhg: 0.001,
    half_life_days: 1825,
    half_life_aerobic_days: 730,
    half_life_anaerobic_days: 3650,
    retardation_factor: 10.0,
    relative_plume_length: 0.1,
    plume_color: '#78716c',  // Stone gray
  },
];

// =============================================================================
// DISPLAY COMPOUND FILTER
// Only these compounds generate plumes, appear in legends, well logs, and filters.
// =============================================================================

// Exact compound names (BTEX + aggregates) — prevents "chlorobenzene" matching "benzene"
const DISPLAY_COMPOUNDS_EXACT = new Set([
  'benzene', 'toluene', 'ethylbenzene',
  'xylenes', 'xylene', 'total xylenes', 'm,p-xylenes', 'o-xylene', 'p-xylene', 'm-xylene',
  'xylenes (total)', 'total xylenes (btex)',
  'total btex', 'btex',
  'total tph', 'tph',
  'tph-gro', 'tph-dro',   // TPH fraction shortcodes
  'gro', 'dro',
]);

// Substring patterns (TPH fractions) — carbon ranges + common lab naming
const DISPLAY_CARBON_PATTERNS: string[] = [
  'c6-c12', 'c6-c10',     // GRO
  'c12-c28', 'c10-c28',   // DRO
  '>c12-c28',
  'c28-c35',               // MOR
  'c6-c35', 'c6-c40',     // Total TPH ranges
  'gasoline range',        // "Gasoline Range Organics" = GRO = C6-C12
  'diesel range',          // "Diesel Range Organics" = DRO = C12-C28
  'motor oil range',       // "Motor Oil Range" = C28-C35
];

/**
 * Check if a compound should be displayed in plumes, legends, and well logs.
 * Uses exact matching for BTEX (prevents false positives like "chlorobenzene")
 * and substring matching for carbon range fractions (unambiguous).
 */
export function isDisplayCompound(compoundName: string): boolean {
  const name = compoundName.toLowerCase().trim();
  if (DISPLAY_COMPOUNDS_EXACT.has(name)) return true;
  return DISPLAY_CARBON_PATTERNS.some(pattern => name.includes(pattern));
}

// Well effect zone for visualization (capture zones, ROI)
export interface WellEffect {
  type: 'recovery' | 'mdpe';
  x_ft: number;
  y_ft: number;
  pump_rate_gpd?: number;  // For recovery wells
  extraction_cfm?: number;  // For MDPE wells
  start_month: number;
  capture_width_ft?: number;  // For recovery wells - width of capture zone
  stagnation_distance_ft?: number;  // Distance to stagnation point upgradient
  roi_ft: number;  // Radius of influence
}

export interface ThreePlumeResponse {
  // Legacy single contaminant field
  contaminant_type: string;
  simulation_months: number;
  remediation_type: string;

  // Summary statistics (primary/first contaminant for backwards compatibility)
  // All fields are optional - will be null if no PSH data is available
  initial_psh_mass_lbs: number | null;
  final_psh_mass_lbs: number | null;
  psh_depletion_percent: number | null;
  peak_dissolved_area_sqft: number | null;
  peak_dissolved_conc_ug_l: number | null;
  time_to_peak_dissolved_months: number | null;
  peak_vapor_area_sqft: number | null;
  peak_vapor_conc_ug_m3: number | null;
  time_to_psh_depletion_months: number | null;
  time_to_mcl_months: number | null;

  // Time series data (primary contaminant)
  plume_states: ThreePlumeState[];

  // Well effect zones for visualization
  well_effects?: WellEffect[];

  // Multi-contaminant results
  contaminant_results?: ContaminantResult[];

  // PSH data availability metadata
  psh_data_source?: string | null;  // "request", "vault/gauging", "site_plume_area", "none"
  psh_data_available?: boolean;  // false if no PSH data was found

  // Pre-computed boundary polygons per time step for efficient rendering
  // Keyed by time_months -> array of ContaminantBoundaryData-compatible objects
  precomputed_boundaries?: Record<number, ContaminantBoundaryData[]>;
}

export interface RemediationOption {
  id: string;
  name: string;
  description: string;
  effectiveness: {
    psh_removal_pct_per_month: number;
    dissolved_removal_pct_per_month: number;
    vapor_removal_pct_per_month: number;
  };
  best_for: string[];
  typical_duration_months: string;
}

export interface ThreePlumeContaminant {
  id: string;
  name: string;
  molecular_weight: number;
  pure_solubility_mg_L: number;
  vapor_pressure_mmHg: number;
  henrys_constant: number;
  log_koc: number;
  biodeg_rate_gw_per_day: number;
  biodeg_rate_soil_per_day: number;
  mcl_ug_L: number;
  volatility: string;
  mobility: string;
}

// ============================================================================
// PSH (Phase Separated Hydrocarbon) TYPES
// ============================================================================

// ============================================================================
// CONCENTRATION HISTORY (for Historical Charts)
// ============================================================================

export interface ConcentrationHistoryPoint {
  sample_date: string;
  max_concentration_ugl: number;
  sample_count: number;
}

export interface ConcentrationHistoryResponse {
  site_id: string;
  compound_group: string;
  data_points: ConcentrationHistoryPoint[];
  total_samples: number;
}

export interface PSHWellMeasurement {
  well_id: string;
  well_name: string | null;
  lat: number;
  lon: number;
  gauge_date: string;
  depth_to_water_ft: number | null;
  depth_to_product_ft: number | null;
  product_thickness_ft: number;
  water_elevation_ft: number | null;
  is_inferred?: boolean;
}

export interface PSHWellSummary {
  well_id: string;
  well_name: string | null;
  lat: number;
  lon: number;
  latest_gauge_date: string;
  latest_thickness_ft: number;
  max_thickness_ft: number;
  measurement_count: number;
  depth_to_water_ft: number | null;
  depth_to_product_ft: number | null;
}

export interface PSHSiteData {
  site_id: string;
  site_name: string;
  has_psh: boolean;
  total_wells_with_psh: number;
  max_thickness_ft: number;
  avg_thickness_ft: number;
  latest_measurement_date: string | null;
  wells: PSHWellSummary[];
  all_measurements: PSHWellMeasurement[];
  bounds: {
    min_lat: number;
    max_lat: number;
    min_lon: number;
    max_lon: number;
  };
}

// ============================================================================
// API FUNCTIONS
// ============================================================================

export const polygonApi = {
  /**
   * Run full POLYGON analysis on a site
   */
  async analyzeSite(request: PolygonAnalysisRequest): Promise<PolygonOutput> {
    const response = await apiClient.post<PolygonOutput>('/polygon/ml/analyze', request);
    return response.data;
  },

  /**
   * Get quick cost estimate with minimal inputs
   */
  async quickEstimate(request: QuickEstimateRequest): Promise<QuickEstimateResponse> {
    const response = await apiClient.post<QuickEstimateResponse>('/polygon/ml/quick-estimate', request);
    return response.data;
  },

  /**
   * Search for existing wells near coordinates
   */
  async searchWells(
    latitude: number,
    longitude: number,
    radiusFt: number = 2640
  ): Promise<WellSearchResult> {
    const response = await apiClient.get<WellSearchResult>('/polygon/ml/wells/search', {
      params: { latitude, longitude, radius_ft: radiusFt }
    });
    return response.data;
  },

  /**
   * Get sites in portfolio (paginated)
   */
  async getSites(page: number = 1, pageSize: number = 20, search?: string): Promise<PaginatedSitesResponse> {
    const response = await apiClient.get<PaginatedSitesResponse>('/polygon/ml/sites', {
      params: { page, page_size: pageSize, ...(search ? { search } : {}) },
    });
    return response.data;
  },

  /**
   * Get site by ID
   */
  async getSite(siteId: string): Promise<PolygonOutput> {
    const response = await apiClient.get<PolygonOutput>(`/polygon/ml/sites/${siteId}`);
    return response.data;
  },

  /**
   * Get portfolio summary
   */
  async getPortfolioSummary(): Promise<PortfolioSummary> {
    const response = await apiClient.get<PortfolioSummary>('/polygon/ml/portfolio/summary');
    return response.data;
  },

  /**
   * Delete a site from portfolio
   */
  async deleteSite(siteId: string): Promise<void> {
    await apiClient.delete(`/polygon/ml/sites/${siteId}`);
  },

  /**
   * Get cost parameters used in calculations
   */
  async getCostParameters(): Promise<Record<string, number>> {
    const response = await apiClient.get<Record<string, number>>('/polygon/ml/cost-parameters');
    return response.data;
  },

  /**
   * Get model performance metrics
   */
  async getModelInfo(): Promise<Record<string, unknown>> {
    const response = await apiClient.get<Record<string, unknown>>('/polygon/ml/models/info');
    return response.data;
  },

  /**
   * Run FLOPY-based plume simulation
   * Returns physics-based groundwater modeling predictions
   */
  async runSimulation(request: SimulationRequest): Promise<SimulationResponse> {
    const response = await apiClient.post<SimulationResponse>('/polygon/ml/simulate', request);
    return response.data;
  },

  /**
   * Fetch hydrogeological data for coordinates
   * Auto-populates form with USGS toolkit data
   */
  async fetchSiteData(latitude: number, longitude: number): Promise<HydroData> {
    const response = await apiClient.get<HydroData>('/polygon/ml/site-data', {
      params: { latitude, longitude }
    });
    return response.data;
  },

  /**
   * Get site details by ID with simulation
   */
  async getSiteDetails(siteId: string): Promise<SiteDetailsResponse> {
    const response = await apiClient.get<SiteDetailsResponse>(`/polygon/ml/sites/${siteId}`);
    return response.data;
  },

  /**
   * Get all data needed for the site detail page in a single request.
   * Replaces 10+ separate API calls with one consolidated bundle.
   */
  async getPageBundle(siteId: string): Promise<PageBundleResponse> {
    const response = await apiClient.get<PageBundleResponse>(`/polygon/ml/sites/${siteId}/page-bundle`);
    return response.data;
  },

  /**
   * Run combined Physics + ML analysis
   * Returns both FLOPY simulation and XGBoost predictions together
   * Note: This can take 1-3 minutes due to MODFLOW 6 simulation
   */
  async runCombinedAnalysis(request: CombinedAnalysisRequest): Promise<CombinedAnalysisResponse> {
    const response = await apiClient.post<CombinedAnalysisResponse>('/polygon/ml/analyze/combined', request, {
      timeout: 180000, // 3 minute timeout for MODFLOW simulation
    });
    return response.data;
  },

  /**
   * Upload bulk sites from CSV
   */
  async uploadSitesCsv(file: File): Promise<{ uploaded: number; errors: string[] }> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post<{ uploaded: number; errors: string[] }>(
      '/polygon/sites/upload',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  },

  // ==========================================================================
  // THREE-PLUME MODEL (PSH, Dissolved, Vapor)
  // ==========================================================================

  /**
   * Run coupled three-plume simulation for petroleum hydrocarbon sites.
   * Models the evolution of PSH/LNAPL source, dissolved plume, and vapor plume.
   */
  async runThreePlumeSimulation(request: ThreePlumeRequest): Promise<ThreePlumeResponse> {
    const response = await apiClient.post<ThreePlumeResponse>('/polygon/ml/three-plume/simulate', request);
    return response.data;
  },

  /**
   * Get available remediation technologies and their effectiveness.
   */
  async getRemediationOptions(): Promise<{ remediation_options: RemediationOption[] }> {
    const response = await apiClient.get<{ remediation_options: RemediationOption[] }>(
      '/polygon/ml/three-plume/remediation-options'
    );
    return response.data;
  },

  /**
   * Get available contaminants for three-plume model with properties.
   */
  async getThreePlumeContaminants(): Promise<{ contaminants: ThreePlumeContaminant[] }> {
    const response = await apiClient.get<{ contaminants: ThreePlumeContaminant[] }>(
      '/polygon/ml/three-plume/contaminants'
    );
    return response.data;
  },

  // ==========================================================================
  // EXTRACTED DATA FROM DOCUMENT VAULT
  // ==========================================================================

  /**
   * Get all data extracted from Document Vault for a site.
   * Includes wells, analytical results, and plume snapshots.
   */
  async getSiteExtractedData(siteId: string): Promise<ExtractedDataResponse> {
    const response = await apiClient.get<ExtractedDataResponse>(
      `/polygon/ml/sites/${siteId}/extracted-data`
    );
    return response.data;
  },

  /**
   * Get analytical result history for a site.
   * Use for charting concentration trends over time.
   */
  async getAnalyticalHistory(
    siteId: string,
    parameter?: string,
    wellId?: string
  ): Promise<AnalyticalHistoryResponse> {
    const response = await apiClient.get<AnalyticalHistoryResponse>(
      `/polygon/ml/sites/${siteId}/analytical-history`,
      { params: { parameter, well_id: wellId } }
    );
    return response.data;
  },

  // ==========================================================================
  // MERGED INPUTS (Vault + Toolkit Integration)
  // ==========================================================================

  /**
   * Get merged inputs for 3-plume model with source tracking.
   * Combines vault extractions with USGS/TWDB toolkit fallbacks.
   * Each field includes its source for UI source badges.
   */
  async getMergedInputs(siteId: string): Promise<MergedSiteInputs> {
    const response = await apiClient.get<MergedSiteInputs>(
      `/polygon/ml/sites/${siteId}/merged-inputs`
    );
    return response.data;
  },

  // ==========================================================================
  // BATCH GENERATION FROM VAULT
  // ==========================================================================

  /**
   * Batch generate Polygon sites from all Vault sites.
   * Returns a job ID for tracking progress.
   */
  async generateFromVault(
    request: GenerateFromVaultRequest = {}
  ): Promise<GenerateFromVaultResponse> {
    const response = await apiClient.post<GenerateFromVaultResponse>(
      '/polygon/ml/generate-from-vault',
      request
    );
    return response.data;
  },

  /**
   * Get progress of a batch generation job.
   */
  async getGenerationProgress(jobId: string): Promise<GenerateFromVaultProgress> {
    const response = await apiClient.get<GenerateFromVaultProgress>(
      `/polygon/ml/generate-from-vault/${jobId}`
    );
    return response.data;
  },

  // ==========================================================================
  // GRADIENT MAP METHODS
  // ==========================================================================

  /**
   * Get the gradient map data for a site.
   * Returns extracted contours and flow data for visualization and physics.
   */
  async getSiteGradientMap(siteId: string): Promise<SiteGradientMap | null> {
    const response = await apiClient.get<SiteGradientMap | null>(
      `/polygon/ml/sites/${siteId}/gradient-map`
    );
    return response.data;
  },

  /**
   * IDW + marching-squares water-table contours — drop-in port of Phreatic's
   * Well Map contour pipeline. We fetch the well gauging readings from the
   * Polygon backend, then run **Phreatic's exact client-side ContourGenerator**
   * (16-case marching squares + segment stitching into connected polylines)
   * so the rendered output is byte-identical in shape to Phreatic's. The
   * backend's simpler `_extract_contour_coords` output is intentionally
   * discarded — it returns a flat bag of crossing points rather than smooth
   * polylines, and that's why earlier copies of the algorithm still looked
   * worse than Phreatic on Polygon.
   *
   * Pass `includeGrid=true` to also return the raw IDW grid + lat/lon axes
   * for the 3D map's water-table surface mesh.
   */
  async getWaterTableContours(
    siteId: string,
    opts?: { gaugeDate?: string; includeGrid?: boolean },
  ): Promise<(SiteGradientMap & {
    grid_elevation?: number[][];
    grid_lat?: number[];
    grid_lon?: number[];
  }) | null> {
    const { generateContours } = await import('../components/phreatic/ContourGenerator');

    const params: Record<string, string | boolean> = {};
    if (opts?.gaugeDate) params.gauge_date = opts.gaugeDate;
    if (opts?.includeGrid) params.include_grid = true;
    const response = await apiClient.get<{
      site_id: string;
      gauge_date: string | null;
      wells: Array<{ well_id: string; well_name: string | null; latitude: number; longitude: number; water_elevation_ft: number; gauge_date: string | null }>;
      contours: Array<{ elevation_ft: number; coordinates: [number, number][] }>;
      flow_direction_deg: number | null;
      flow_magnitude: number | null;
      grid_bounds: { min_lat: number; max_lat: number; min_lon: number; max_lon: number } | null;
      grid_elevation?: number[][];
      grid_lat?: number[];
      grid_lon?: number[];
      note?: string;
    }>(`/polygon/sites/${siteId}/water-table-contours`, { params });

    const data = response.data;
    if (!data || data.wells.length < 3) return null;

    // Run Phreatic's exact client-side contour generator on the raw well
    // elevations. Grid resolution 50 + 12 contour levels match Phreatic's
    // WaterTableMap.tsx call signature so the output is visually identical.
    const wellElevs = data.wells
      .filter(w => Number.isFinite(w.latitude) && Number.isFinite(w.longitude) && Number.isFinite(w.water_elevation_ft) && w.water_elevation_ft > 50)
      .map(w => ({ lat: w.latitude, lon: w.longitude, waterElev: w.water_elevation_ft }));
    const phreaticContours = generateContours(wellElevs, 50, 12);
    if (phreaticContours.length === 0) return null;

    // Adapt to the SiteGradientMap shape consumed by GradientContourLayer
    // (2D) and ThreePlume3DView's contour memo (3D). Both expect
    // `contours[i] = { elevation_ft, coordinates: [[lat, lon], ...] }`.
    const centroidLat = data.wells.length
      ? data.wells.reduce((s, w) => s + w.latitude, 0) / data.wells.length
      : 0;
    const centroidLon = data.wells.length
      ? data.wells.reduce((s, w) => s + w.longitude, 0) / data.wells.length
      : 0;
    const elevations = phreaticContours.map(c => c.elevation);
    return {
      id: `idw-${siteId}`,
      site_id: siteId,
      source: 'interpolated',
      contours: phreaticContours.map(c => ({
        elevation_ft: c.elevation,
        coordinates: c.points as [number, number][],
      })),
      flow_arrows: data.flow_direction_deg != null && data.wells.length > 0 ? [{
        lat: centroidLat,
        lon: centroidLon,
        direction_deg: data.flow_direction_deg,
        magnitude: data.flow_magnitude ?? null,
      }] : [],
      well_readings: data.wells.map(w => ({
        well_id: w.well_id,
        lat: w.latitude,
        lon: w.longitude,
        wt_elev_ft: w.water_elevation_ft,
      })),
      average_gradient: data.flow_magnitude ?? undefined,
      average_flow_direction_deg: data.flow_direction_deg ?? undefined,
      min_elevation_ft: elevations.length ? Math.min(...elevations) : undefined,
      max_elevation_ft: elevations.length ? Math.max(...elevations) : undefined,
      coordinates_type: 'absolute',
      has_gradient_grid: !!data.grid_elevation,
      grid_elevation: data.grid_elevation,
      grid_lat: data.grid_lat,
      grid_lon: data.grid_lon,
    };
  },

  /**
   * Get just the contour lines for efficient map overlay rendering.
   */
  async getGradientContours(siteId: string): Promise<GradientContour[]> {
    const response = await apiClient.get<GradientContour[]>(
      `/polygon/ml/sites/${siteId}/gradient-map/contours`
    );
    return response.data;
  },

  /**
   * Query the gradient field at a specific point.
   * Uses bilinear interpolation on the gradient grid.
   */
  async queryGradientAtPoint(
    siteId: string,
    request: GradientAtPointRequest
  ): Promise<GradientAtPointResponse> {
    const response = await apiClient.post<GradientAtPointResponse>(
      `/polygon/ml/sites/${siteId}/gradient-map/query-point`,
      request
    );
    return response.data;
  },

  /**
   * Compute gradient field from well water level measurements.
   * This is a fallback when no gradient map is uploaded - uses IDW interpolation.
   */
  async computeGradientFromWells(siteId: string): Promise<TriggerGradientResponse> {
    const response = await apiClient.post<TriggerGradientResponse>(
      `/polygon/ml/sites/${siteId}/gradient-map/compute-from-wells`
    );
    return response.data;
  },

  // ==========================================================================
  // CONCENTRATION PLUME BOUNDARY (Data-Driven)
  // ==========================================================================

  /**
   * Get a data-driven plume boundary based on well concentration data.
   *
   * Generates a smooth plume polygon with arms reaching to all wells
   * that have concentrations above the threshold (default: 30 µg/L = 0.03 mg/L).
   *
   * Uses Chaikin smoothing for natural curve shapes.
   */
  async getConcentrationPlumeBoundary(
    siteId: string,
    contaminant: string = 'benzene',
    thresholdUgL: number = 30.0
  ): Promise<ConcentrationPlumeBoundaryResponse> {
    const response = await apiClient.get<ConcentrationPlumeBoundaryResponse>(
      `/polygon/ml/sites/${siteId}/concentration-plume-boundary`,
      { params: { contaminant, threshold_ug_l: thresholdUgL } }
    );
    return response.data;
  },

  /**
   * Get plume boundaries for ALL contaminants detected at a site.
   *
   * Two modes available:
   * 1. Well-based (default): Convex hull of wells exceeding MCL threshold
   * 2. Physics-based (usePhysics=true): Domenico analytical solution to calculate
   *    plume extent BEYOND wells where concentration >= MCL
   *
   * The physics mode allows plumes to extend downgradient from the well network
   * based on advection-dispersion transport calculations. Wells serve as
   * validation points in a continuous concentration field.
   *
   * @param siteId - Site UUID
   * @param thresholdMultiplier - Multiplier for MCL threshold (1.0 = MCL)
   * @param usePhysics - If true, extend plumes beyond wells using Domenico physics
   * @param targetDate - Optional target date for physics prediction (ISO format)
   */
  async getAllContaminantPlumeBoundaries(
    siteId: string,
    thresholdMultiplier: number = 1.0,
    usePhysics: boolean = false,
    targetDate?: string,
    librarySlug?: string,
  ): Promise<AllContaminantBoundariesResponse> {
    const params: Record<string, unknown> = {
      threshold_multiplier: thresholdMultiplier,
      use_physics: usePhysics,
    };
    if (targetDate) {
      params.target_date = targetDate;
    }
    if (librarySlug) {
      params.library_slug = librarySlug;
    }
    const response = await apiClient.get<AllContaminantBoundariesResponse>(
      `/polygon/ml/sites/${siteId}/all-contaminant-plume-boundaries`,
      { params }
    );
    return response.data;
  },

  /**
   * Calculate groundwater gradient from well data using SOP T604 methodology.
   *
   * Implements Talon LPE Standard Operating Procedure T604 (Groundwater Gradient Maps)
   * to generate contour lines and flow vectors from static water level measurements.
   *
   * Key Formula (Linear Interpolation):
   *   D₁ × (W₁ - C) / (W₁ - W₂) = D₂
   *
   * Requirements:
   * - Minimum 3 wells (not collinear)
   * - All wells must be gauged on the same date
   * - Wells should be in roughly triangular arrangement
   */
  async calculateGradientFromWells(
    request: CalculateGradientRequest
  ): Promise<CalculateGradientResponse> {
    const response = await apiClient.post<CalculateGradientResponse>(
      '/polygon/ml/gradient/calculate',
      request
    );
    return response.data;
  },

  /**
   * Generate gradient + plume + wells from stored Vault well data.
   *
   * This is the main integration endpoint that:
   * 1. Pulls well locations from MonitoringWell table
   * 2. Pulls water levels from WellGauging table
   * 3. Pulls concentrations from AnalyticalResult table
   * 4. Calculates gradient using SOP T604
   * 5. Generates plume boundaries using IDW interpolation
   * 6. Returns wells organized by type (monitoring, recovery, MDPE)
   *
   * Use this to auto-populate the POLYGON form from Vault data.
   */
  async generateFromWells(
    siteId: string,
    request: GenerateFromWellsRequest = {}
  ): Promise<GenerateFromWellsResponse> {
    const response = await apiClient.post<GenerateFromWellsResponse>(
      `/polygon/ml/sites/${siteId}/generate-from-wells`,
      request
    );
    return response.data;
  },

  /**
   * Query TWDB (Texas Water Development Board) wells near a location
   */
  async queryTWDBWells(
    latitude: number,
    longitude: number,
    radiusMiles: number = 1.0
  ): Promise<TWDBQueryResponse> {
    const response = await apiClient.get<TWDBQueryResponse>('/twdb/wells', {
      params: { lat: latitude, lng: longitude, radius: radiusMiles }
    });
    return response.data;
  },

  // ==========================================================================
  // CONCENTRATION PREDICTIONS
  // ==========================================================================

  /**
   * Get latest per-well concentration predictions for a site.
   */
  async getWellPredictions(
    siteId: string,
    page: number = 1,
    pageSize: number = 100,
    analyteCode?: string,
    analyteGroup?: string,
    librarySlug?: string,
  ): Promise<WellPredictionsResponse> {
    const params: Record<string, unknown> = { page, page_size: pageSize };
    if (analyteCode) params.analyte_code = analyteCode;
    if (analyteGroup) params.analyte_group = analyteGroup;
    if (librarySlug) params.library_slug = librarySlug;
    const response = await apiClient.get<WellPredictionsResponse>(
      `/concentration/sites/${siteId}/well-predictions`,
      { params }
    );
    return response.data;
  },

  /**
   * Get well predictions grouped by v3.2 calendar horizon. The reg-library
   * slug is honored at response time — the API re-scores `mcl_mgl` and
   * `exceedance_probability` per row against the chosen library so toggling
   * the dropdown doesn't require re-running inference.
   */
  async getTimelinePredictions(siteId: string, librarySlug?: string): Promise<TimelinePredictionsResponse> {
    const params: Record<string, unknown> = {};
    if (librarySlug) params.library_slug = librarySlug;
    const response = await apiClient.get<TimelinePredictionsResponse>(
      `/concentration/sites/${siteId}/timeline-predictions`,
      { params },
    );
    return response.data;
  },

  /**
   * Terra/Gemini-driven capital allocation across the polygon portfolio.
   * Considers DW install + 24mo remediation + plume expansion penalty per site.
   */
  async recommendCapitalAllocation(
    req: CapitalRecommendationRequest,
  ): Promise<CapitalRecommendationResponse> {
    const response = await apiClient.post<CapitalRecommendationResponse>(
      `/polygon/ml/capital/recommend-allocation`,
      req,
    );
    return response.data;
  },

  /**
   * Time-varying water-table gradient generated from per-date DTW gauging
   * events. Returns a SiteGradientMap-shaped payload — drop it straight into
   * ThreePlumeMap / ThreePlume3DView's `gradientMap` prop so contours render
   * under the plume layers.
   */
  async getWaterTableAtDate(
    siteId: string,
    gaugeDate?: string,
  ): Promise<SiteGradientMap | null> {
    const response = await apiClient.get<SiteGradientMap | null>(
      `/polygon/ml/sites/${siteId}/water-table-at-date`,
      { params: gaugeDate ? { gauge_date: gaugeDate } : {} },
    );
    return response.data;
  },

  /** List of gauge dates (with well counts) that have enough data for contours. */
  async getGaugingDates(
    siteId: string,
  ): Promise<{ site_id: string; dates: { date: string; well_count: number }[] }> {
    const response = await apiClient.get<{ site_id: string; dates: { date: string; well_count: number }[] }>(
      `/polygon/ml/sites/${siteId}/gauging-dates`,
    );
    return response.data;
  },

  /** List regulatory libraries available for the polygon reg-limit dropdown. */
  async listRegLibraries(): Promise<RegLibrariesResponse> {
    const response = await apiClient.get<RegLibrariesResponse>(`/polygon/ml/reg-libraries`);
    return response.data;
  },

  /** Get BTEX + TPH key MCLs for the chosen library — drives the reference card. */
  async getRegLibraryKeyMcls(slug: string): Promise<RegLibraryKeyMclsResponse> {
    const response = await apiClient.get<RegLibraryKeyMclsResponse>(`/polygon/ml/reg-libraries/${slug}/key-mcls`);
    return response.data;
  },

  /**
   * Real-data-driven values for the 5 contaminant cards (lead contaminant,
   * initial PSH, PSH depleted, peak dissolved, remediation). Each field is
   * `{value, as_of_date, source_note}` and `value` may be null when there's
   * not enough data — the UI then falls back to the three-plume simulation.
   */
  async getObservedMetrics(siteId: string, librarySlug?: string): Promise<ObservedMetricsResponse> {
    const params: Record<string, string> = {};
    if (librarySlug) params.library_slug = librarySlug;
    const response = await apiClient.get<ObservedMetricsResponse>(
      `/polygon/ml/sites/${siteId}/observed-metrics`,
      { params },
    );
    return response.data;
  },

  /**
   * Get site-level concentration summary with closure timeline.
   */
  async getClosureTimeline(siteId: string): Promise<SiteConcentrationSummary> {
    const response = await apiClient.get<SiteConcentrationSummary>(
      `/concentration/sites/${siteId}/closure-timeline`
    );
    return response.data;
  },

  /**
   * Trigger generation of multi-step predictions for timeline slider.
   */
  async generatePredictions(
    siteId: string,
    quartersAhead: number = 40
  ): Promise<{ site_id: string; predictions: number; quarters_ahead: number; wells_processed: number; message: string }> {
    const response = await apiClient.post<{ site_id: string; predictions: number; quarters_ahead: number; wells_processed: number; message: string }>(
      `/concentration/sites/${siteId}/generate-predictions`,
      { quarters_ahead: quartersAhead }
    );
    return response.data;
  },

  // ==========================================================================
  // PSH (Phase Separated Hydrocarbon) DATA
  // ==========================================================================

  /**
   * Get PSH/LNAPL well data for 3D voxel visualization.
   * Returns wells with product_thickness_ft > 0 and their coordinates.
   */
  async getPSHSiteData(siteId: string, includeHistory: boolean = true): Promise<PSHSiteData> {
    const response = await apiClient.get<PSHSiteData>(
      `/polygon/psh/site/${siteId}`,
      { params: { include_history: includeHistory } }
    );
    return response.data;
  },

  /**
   * Get latest PSH measurement per well (optimized for real-time visualization).
   */
  async getLatestPSHMeasurements(siteId: string): Promise<PSHWellMeasurement[]> {
    const response = await apiClient.get<PSHWellMeasurement[]>(
      `/polygon/psh/site/${siteId}/latest`
    );
    return response.data;
  },

  /**
   * Get PSH wells summary (without full history).
   */
  async getPSHWells(siteId: string): Promise<PSHWellSummary[]> {
    const response = await apiClient.get<PSHWellSummary[]>(
      `/polygon/psh/site/${siteId}/wells`
    );
    return response.data;
  },

  /**
   * Get sentinel alerts with optional time-aware analysis.
   * When targetDate is provided, uses historical plume state at that date.
   */
  async getSentinelAlerts(
    siteId: string,
    targetDate?: string,
    librarySlug?: string,
  ): Promise<SentinelAlertsResponse> {
    const params: Record<string, string> = {};
    if (targetDate) params.target_date = targetDate;
    if (librarySlug) params.library_slug = librarySlug;
    const response = await apiClient.get<SentinelAlertsResponse>(
      `/polygon/ml/sites/${siteId}/sentinel-alerts`, { params }
    );
    return response.data;
  },

  /**
   * Get historical concentration data for charting (max BTEX per sample date).
   */
  async getConcentrationHistory(siteId: string, compoundGroup = 'btex'): Promise<ConcentrationHistoryResponse> {
    const response = await apiClient.get<ConcentrationHistoryResponse>(
      `/polygon/sites/${siteId}/concentration-history`,
      { params: { compound_group: compoundGroup } }
    );
    return response.data;
  },
};

// ============================================================================
// EXTRACTED DATA TYPES
// ============================================================================

export interface ExtractedWell {
  well_id: string;
  well_name?: string;
  well_type: string | null;
  latitude: number | null;
  longitude: number | null;
  ground_elevation_ft: number | null;
  total_depth_ft: number | null;
  screen_top_ft: number | null;
  screen_bottom_ft: number | null;
  is_active: boolean;
  install_date: string | null;
}

export interface ExtractedGauging {
  well_id: string;
  gauge_date: string | null;
  depth_to_water_ft: number | null;
  water_elevation_ft: number | null;
  depth_to_product_ft: number | null;
  product_thickness_ft: number | null;
  is_dry: boolean;
  measuring_point: string | null;
}

export interface ExtractedAnalyticalResult {
  well_id: string | null;
  sample_date: string | null;
  parameter: string;
  concentration_ug_l: number | null;
  units: string;
  is_non_detect: boolean;
  mcl: number | null;
  exceeds_mcl: boolean | null;
}

export interface ExtractedPlumeSnapshot {
  snapshot_date: string | null;
  plume_area_sqft: number | null;
  plume_length_ft: number | null;
  plume_width_ft: number | null;
  is_measured: boolean;
}

export interface ExtractedDataSummary {
  total_wells: number;
  total_gaugings: number;
  total_analytical_results: number;
  total_plume_snapshots: number;
  latest_sample_date: string | null;
  latest_gauging_date: string | null;
  parameters_detected: string[];
  max_concentration_ug_l: number | null;
  max_concentration_parameter: string | null;
}

export interface ExtractedDataResponse {
  site_id: string;
  site_name: string;
  wells: ExtractedWell[];
  gaugings: ExtractedGauging[];
  analytical_results: ExtractedAnalyticalResult[];
  plume_snapshots: ExtractedPlumeSnapshot[];
  summary: ExtractedDataSummary;
}

// ============================================================================
// TWDB (Texas Water Development Board) WELL TYPES
// ============================================================================

export interface TWDBWell {
  state_well_number: string;
  owner_name: string | null;
  well_type: string | null;
  well_depth_ft: number | null;
  elevation_ft: number | null;
  latitude: number;
  longitude: number;
  county: string | null;
  aquifer_name: string | null;
  water_level_obs_type: string | null;
  water_quality_available: string | null;
  distance_miles: number | null;
}

export interface TWDBQueryResponse {
  wells: TWDBWell[];
  total_count: number;
  query_center_lat: number;
  query_center_lng: number;
  radius_miles: number;
  exceeded_limit: boolean;
  message: string;
}

export interface AnalyticalDataPoint {
  date: string | null;
  well_id: string | null;
  concentration: number;
  is_non_detect: boolean;
  mcl: number | null;
}

export interface AnalyticalHistoryResponse {
  site_id: string;
  site_name: string;
  filter: {
    parameter: string | null;
    well_id: string | null;
  };
  data: Record<string, AnalyticalDataPoint[]>;
  total_records: number;
}

// ============================================================================
// MERGED INPUTS TYPES (Vault + Toolkit Integration)
// ============================================================================

export type DataSource = 'vault' | 'usgs' | 'twdb' | 'site_table' | 'user' | 'default';

export interface SourcedValue<T> {
  value: T;
  source: DataSource;
  confidence: number;
  extraction_date?: string | null;
}

export interface ContaminantWithSource {
  type: string;
  concentration_ug_l: number;
  source: DataSource;
  well_id?: string | null;
  sample_date?: string | null;
  original_compound?: string | null;
}

export interface WellWithSource {
  well_id: string;
  latitude: number;
  longitude: number;
  well_type: string;
  source: DataSource;
  pump_rate_gpd?: number | null;
  extraction_cfm?: number | null;
  x_ft?: number | null;
  y_ft?: number | null;
}

export interface PSHData {
  has_psh: boolean;
  thickness_ft?: SourcedValue<number> | null;
  area_sqft?: SourcedValue<number> | null;
  wells_with_product: number;
}

export interface MergedSiteInputs {
  site_id: string;
  site_name: string;
  latitude: number;
  longitude: number;

  // Hydrogeology with source tracking
  hydraulic_conductivity_ft_day: SourcedValue<number>;
  porosity: SourcedValue<number>;
  hydraulic_gradient: SourcedValue<number>;
  flow_direction_degrees: SourcedValue<number>;
  depth_to_water_ft: SourcedValue<number>;
  aquifer_thickness_ft: SourcedValue<number>;
  organic_carbon_foc: SourcedValue<number>;
  soil_type: SourcedValue<string>;

  // PSH/LNAPL data
  psh_data: PSHData;

  // Contaminants mapped to 3-plume types
  contaminants: ContaminantWithSource[];

  // Wells with positions
  wells: WellWithSource[];

  // Plume geometry
  plume_area_sqft?: SourcedValue<number> | null;
  plume_length_ft?: SourcedValue<number> | null;
  plume_width_ft?: SourcedValue<number> | null;
  max_concentration_ug_l?: SourcedValue<number> | null;

  // Metadata
  data_sources_used: DataSource[];
  fetch_timestamp: string;
}

// ============================================================================
// BATCH GENERATION TYPES
// ============================================================================

export interface GenerateFromVaultRequest {
  client_id?: string;
  include_sites_without_extractions?: boolean;
  sync?: boolean;  // Run synchronously (default true for dev)
}

export interface GenerateFromVaultResponse {
  job_id: string;
  status: string;
  message: string;
  total_sites: number;
  created_site_ids: string[];
}

export interface GenerateFromVaultProgress {
  job_id: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'not_found';
  total_sites: number;
  completed_sites: number;
  failed_sites: number;
  current_site: string | null;
  created_site_ids: string[];
  errors: string[];
}

// ============================================================================
// GRADIENT MAP TYPES
// ============================================================================

/**
 * A single water table elevation contour line extracted from a gradient map.
 */
export interface GradientContour {
  elevation_ft: number | null; // Can be null if extraction failed for this contour
  coordinates: [number, number][]; // Array of [lat, lon] points
}

/**
 * A flow direction indicator extracted from a gradient map.
 */
export interface FlowArrow {
  lat: number;
  lon: number;
  direction_deg: number | null; // Flow direction in degrees (0=North, 90=East), null if unknown
  magnitude?: number | null;    // Gradient magnitude if available
}

/**
 * Well water level reading extracted from a gradient map.
 */
export interface GradientWellReading {
  well_id: string;
  lat: number;
  lon: number;
  wt_elev_ft: number | null; // Water table elevation in feet, null if unknown
}

// (Aliases for the removed MonitoringWellAnalysis component were here.
// Deleted along with the dead component — was stale code with broken imports.)

/**
 * Full site gradient map data including contours, flow arrows, and metadata.
 */
export interface SiteGradientMap {
  id: string;
  site_id: string;
  source: 'extracted' | 'interpolated';
  contours: GradientContour[];
  flow_arrows?: FlowArrow[];
  well_readings?: GradientWellReading[];
  average_gradient?: number;
  average_flow_direction_deg?: number;
  contour_interval_ft?: number;
  min_elevation_ft?: number;
  max_elevation_ft?: number;
  reference_datum?: string;
  extraction_confidence?: number;
  coordinates_type?: 'absolute' | 'relative';
  map_north_direction?: string;  // "up", "right", "down", "left", etc. - for compass orientation
  has_gradient_grid: boolean;
  created_at?: string;
}

/**
 * Request to query gradient at a specific point.
 */
export interface GradientAtPointRequest {
  lat: number;
  lon: number;
}

/**
 * Response with gradient values at a queried point.
 */
export interface GradientAtPointResponse {
  lat: number;
  lon: number;
  gradient_x: number;        // E-W gradient component
  gradient_y: number;        // N-S gradient component
  magnitude: number;         // Gradient magnitude
  flow_direction_deg: number; // Flow direction in degrees (0=North)
}

/**
 * Response from triggering gradient extraction or computation.
 */
export interface TriggerGradientResponse {
  task_id: string;
  status: string;
  message: string;
}

// ============================================================================
// SOP T604 GRADIENT CALCULATION TYPES (From Monitor Well Data)
// ============================================================================

/**
 * Monitor well input data for gradient calculation.
 * Per SOP T604: Wells must be gauged on the same date, in triangular arrangement.
 */
export interface WellDataInput {
  well_id: string;
  x_ft: number;           // X coordinate in feet (local or easting)
  y_ft: number;           // Y coordinate in feet (local or northing)
  wt_elevation_ft: number; // Water table elevation in feet MSL
  latitude?: number;       // GPS latitude if available
  longitude?: number;      // GPS longitude if available
  status?: 'gauged' | 'dry' | 'NL' | 'NSA' | 'OB'; // Well status
}

/**
 * Request to calculate gradient using SOP T604 methodology.
 */
export interface CalculateGradientRequest {
  wells: WellDataInput[];
  contour_interval_ft?: number;  // Auto-calculated if not specified
  site_center_lat?: number;      // For coordinate conversion
  site_center_lon?: number;
}

/**
 * Calculated contour line from SOP T604.
 */
export interface CalculatedContour {
  elevation_ft: number;
  coordinates: [number, number][]; // [lat, lon] or [y, x] points
}

/**
 * Calculated flow direction vector.
 */
export interface CalculatedFlowArrow {
  lat: number;
  lon: number;
  direction_deg: number;  // 0=North, 90=East
  magnitude: number;      // Gradient magnitude (ft/ft)
}

/**
 * Well reading used in calculation.
 */
export interface CalculatedWellReading {
  well_id: string;
  lat: number;
  lon: number;
  wt_elev_ft: number;
}

/**
 * Complete response from SOP T604 gradient calculation.
 */
export interface CalculateGradientResponse {
  contours: CalculatedContour[];
  flow_arrows: CalculatedFlowArrow[];
  well_readings: CalculatedWellReading[];
  average_gradient: number;           // ft/ft
  average_flow_direction_deg: number; // degrees from north
  contour_interval_ft: number;
  min_elevation_ft: number;
  max_elevation_ft: number;
  wells_used: string[];
  wells_excluded: string[];
  coordinates_type: 'absolute' | 'local';
  calculation_method: string;  // 'sop_t604'
}

// ============================================================================
// GENERATE FROM WELLS TYPES (Vault Integration)
// ============================================================================

/**
 * Request to generate gradient and plume from stored Vault well data.
 */
export interface GenerateFromWellsRequest {
  compound?: string;            // Contaminant for plume (default: "benzene")
  gauge_date?: string;          // Gauging date YYYY-MM-DD (latest if not specified)
  sample_date?: string;         // Sample date YYYY-MM-DD (latest if not specified)
  contour_interval_ft?: number; // Gradient contour interval (auto if not specified)
}

// ============================================================================
// CONCENTRATION PLUME BOUNDARY TYPES (Data-Driven)
// ============================================================================

/**
 * Well with detection info for concentration plume boundary.
 */
export interface WellDetection {
  well_id: string;
  lat: number;
  lon: number;
  concentration: number;
  is_non_detect: boolean;
  date?: string;
}

/**
 * Response from concentration plume boundary endpoint.
 * Contains a smooth plume polygon with arms reaching positive wells.
 */
export interface ConcentrationPlumeBoundaryResponse {
  /** [lat, lon] coordinates forming the plume boundary polygon */
  boundary: [number, number][];
  /** Wells with concentrations above threshold */
  wells_above_threshold: WellDetection[];
  /** Wells with concentrations below threshold */
  wells_below_threshold: WellDetection[];
  /** Contaminant queried */
  contaminant: string;
  /** Concentration threshold in µg/L */
  threshold_ug_l: number;
  /** Groundwater flow direction in degrees */
  flow_direction_deg: number;
  /** Optional message */
  message?: string;
}

/**
 * Boundary data for a single contaminant in multi-COC response.
 */
export interface ContaminantBoundaryData {
  /** Contaminant compound name */
  compound: string;
  /** [lat, lon] coordinates forming the plume boundary polygon */
  boundary: [number, number][];
  /** Wells with detections above threshold */
  wells_with_detections: WellDetection[];
  /** Wells below threshold */
  wells_below_threshold: WellDetection[];
  /** Maximum detected concentration in µg/L */
  max_concentration_ug_l: number;
  /** MCL for this compound in µg/L */
  mcl_ug_l: number;
  /** Threshold used for boundary in µg/L */
  threshold_ug_l: number;
  /** Number of wells with detections */
  well_count: number;
}

/**
 * Response from all-contaminant plume boundaries endpoint.
 * Contains well-based plume boundaries for ALL contaminants at a site.
 */
export interface AllContaminantBoundariesResponse {
  /** Boundary data for each contaminant with detections */
  contaminants: ContaminantBoundaryData[];
  /** Groundwater flow direction in degrees */
  flow_direction_deg: number;
  /** Site center coordinates */
  site_center: { lat: number; lon: number };
  /** Total number of compounds with detections */
  total_compounds: number;
  /** Optional message */
  message?: string;
}

/**
 * A plume boundary from concentration interpolation.
 */
export interface GeneratedPlumeBoundary {
  contaminant: string;
  concentration_ug_l: number;
  is_mcl_contour: boolean;
  coordinates: [number, number][];  // [lat, lon] points
  wells_above: string[];            // Wells above this threshold
}

/**
 * Source zone from plume generation.
 */
export interface GeneratedPlumeSource {
  lat: number;
  lon: number;
  max_concentration_ug_l: number;
  well_id: string;
}

/**
 * Generated plume data from well concentrations.
 */
export interface GeneratedPlumeData {
  success: boolean;
  compound: string;
  sample_date: string;
  plume_boundaries: GeneratedPlumeBoundary[];
  plume_source: GeneratedPlumeSource | null;
  plume_metadata: {
    contaminant_type: string;
    map_date: string;
    mcl_value_ug_l: number;
    contour_levels: number[];
    wells_sampled: number;
  };
  well_concentrations: {
    well_id: string;
    lat: number;
    lon: number;
    concentration: number;
    is_non_detect: boolean;
  }[];
  source: string;  // 'calculated_from_samples'
}

/**
 * Well formatted for simulation with local coordinates.
 */
export interface SimulationWell {
  well_id: string;
  well_type: string;  // 'monitoring', 'recovery', 'mdpe'
  x_ft: number;       // Local coordinates relative to source (downgradient positive)
  y_ft: number;       // Lateral offset
  lat: number;
  lon: number;
  pump_rate_gpd?: number;   // For recovery wells
  extraction_cfm?: number;  // For MDPE wells
  start_month?: number;     // When well starts operating
}

/**
 * All wells organized by type for simulation.
 */
export interface SiteWellsData {
  monitoring_wells: SimulationWell[];  // MW wells - gauging and perimeter detection
  recovery_wells: SimulationWell[];    // RW wells - extract contaminated groundwater
  mdpe_wells: SimulationWell[];        // MDPE wells - extract PSH/LNAPL from source
  all_wells: SimulationWell[];         // Combined for map display
  source_location: { lat: number; lon: number } | null;
  flow_direction_deg: number;
}

/**
 * Metadata about generated site data.
 */
export interface GeneratedSiteMetadata {
  gauge_date: string | null;
  sample_date: string | null;
  compound: string;
  flow_direction_deg: number;
  source_location: { lat: number; lon: number } | null;
  well_counts: {
    monitoring: number;
    recovery: number;
    mdpe: number;
  };
}

/**
 * Complete response from generate-from-wells endpoint.
 * Contains gradient, plume, and wells data for POLYGON form auto-fill.
 */
export interface GenerateFromWellsResponse {
  site_id: string;
  site_name: string | null;
  gradient: CalculateGradientResponse | null;
  plume: GeneratedPlumeData | null;
  wells: SiteWellsData | null;
  metadata: GeneratedSiteMetadata | null;
  errors: string[];
}

// ============================================================================
// EXTRACTED PLUME BOUNDARY TYPES (From AGMR Document Extraction)
// ============================================================================

/**
 * A plume boundary polygon extracted from an AGMR gradient/plume map.
 * These are concentration contours that define the extent of contamination.
 * Used as SEED data for forward time projections in the physics model.
 */
export interface ExtractedPlumeBoundary {
  /** The contaminant this boundary represents */
  contaminant: string;
  /** Concentration value for this contour in µg/L */
  concentration_ug_l: number;
  /** Whether this is the MCL (regulatory limit) boundary */
  is_mcl_contour: boolean;
  /** Polygon coordinates as array of [lat, lon] points - CLOSED polygon */
  coordinates: [number, number][];
  /** Estimated area within this boundary in sq ft (if calculable) */
  area_sqft?: number;
}

/**
 * Source zone information extracted from the plume map.
 * Represents the highest concentration area / origin point.
 */
export interface ExtractedPlumeSource {
  lat: number;
  lon: number;
  max_concentration_ug_l: number;
  /** Source type if identifiable (UST, spill, etc.) */
  source_type?: string;
}

/**
 * Metadata about the extracted plume geometry.
 */
export interface ExtractedPlumeMetadata {
  /** Primary contaminant shown on the map */
  contaminant_type: string;
  /** Date of the plume delineation (YYYY-MM-DD) */
  map_date?: string;
  /** Total plume area above MCL/detection in sq ft */
  total_plume_area_sqft?: number;
  /** Maximum extent along flow direction in ft */
  plume_length_ft?: number;
  /** Maximum extent perpendicular to flow in ft */
  plume_width_ft?: number;
  /** The MCL value for this contaminant in µg/L */
  mcl_value_ug_l?: number;
}

/**
 * Complete extracted plume data from an AGMR document.
 * Includes both gradient (water table) and plume (contamination) data.
 */
export interface ExtractedGradientAndPlumeData {
  success: boolean;

  // Gradient data (water table)
  contours: GradientContour[];
  flow_arrows?: FlowArrow[];
  well_readings?: GradientWellReading[];

  // Plume data (contamination boundaries) - CRITICAL FOR SEEDING PROJECTIONS
  plume_boundaries: ExtractedPlumeBoundary[];
  plume_source?: ExtractedPlumeSource;
  plume_metadata?: ExtractedPlumeMetadata;

  // Map metadata
  metadata?: {
    contour_interval_ft?: number;
    reference_datum?: string;
    map_scale?: string;
    map_date?: string;
  };

  // Calculated values
  calculated_values?: {
    average_gradient?: number;
    average_flow_direction_deg?: number;
    min_elevation_ft?: number;
    max_elevation_ft?: number;
  };

  confidence: number;
  notes?: string;
}

/**
 * Site extracted plume data stored in the database.
 * Used to seed the three-plume physics model with real initial conditions.
 */
export interface SiteExtractedPlume {
  id: string;
  site_id: string;
  document_id?: string;

  /** Date the plume was mapped (from document) */
  plume_date: string;

  /** Primary contaminant */
  contaminant_type: string;

  /** Extracted plume boundaries - nested concentration zones */
  plume_boundaries: ExtractedPlumeBoundary[];

  /** Identified source location */
  source_location?: ExtractedPlumeSource;

  /** Summary geometry */
  total_area_sqft?: number;
  length_ft?: number;
  width_ft?: number;
  max_concentration_ug_l?: number;

  /** Extraction quality */
  extraction_confidence?: number;
  coordinates_type: 'absolute' | 'relative';

  created_at: string;
}

/**
 * Input for seeding a physics simulation with extracted plume geometry.
 * The extracted boundaries define the T=0 state of the plume.
 */
export interface PlumeSeedInput {
  /** Site ID for location context */
  site_id: string;

  /** Use the extracted plume from this document as initial state */
  use_extracted_plume_id?: string;

  /** Or provide custom plume seed geometry */
  custom_seed?: {
    /** Outer boundary (e.g., MCL contour) */
    outer_boundary: [number, number][];
    /** Optional inner boundaries (higher concentration zones) */
    inner_boundaries?: {
      concentration_ug_l: number;
      coordinates: [number, number][];
    }[];
    /** Source location */
    source_lat: number;
    source_lon: number;
    /** Max concentration at source */
    max_concentration_ug_l: number;
    /** Contaminant type */
    contaminant_type: string;
    /** Date this represents (T=0 for projection) */
    seed_date: string;
  };
}

// ============================================================================
// EXTRACTED CONTAMINANT DATA TYPES (From AGMR Lab Reports)
// ============================================================================

/**
 * A single contaminant extracted from lab reports/data tables in an AGMR.
 * Represents the maximum detected concentration of a unique contaminant.
 */
export interface ExtractedContaminant {
  /** Standardized contaminant name (lowercase): benzene, toluene, etc. */
  name: string;
  /** Maximum detected concentration in µg/L */
  concentration_ug_l: number;
  /** Original unit from document */
  unit: string;
  /** Whether concentration exceeds the MCL */
  exceeds_mcl: boolean;
  /** MCL value for this contaminant in µg/L */
  mcl_ug_l: number;
  /** Well where maximum was detected (if identifiable) */
  well_id?: string;
  /** Date of sample (YYYY-MM-DD if available) */
  sample_date?: string;
}

/**
 * Individual well sample data extracted from lab reports.
 */
export interface ExtractedWellSample {
  /** Monitoring well ID */
  well_id: string;
  /** Contaminant name (lowercase) */
  compound: string;
  /** Concentration in µg/L */
  concentration: number;
  /** Sample date (YYYY-MM-DD) */
  sample_date?: string;
  /** Original units from document */
  units: string;
  /** True if non-detect/ND/<DL */
  is_non_detect: boolean;
}

/**
 * Complete AGMR extraction result including gradient, plume, and contaminant data.
 * This is the full extraction output from extract_gradient_and_plume_data().
 */
export interface AGMRExtractionResult extends ExtractedGradientAndPlumeData {
  /** All detected contaminants from lab reports with max concentrations */
  contaminants: ExtractedContaminant[];

  /** Individual well sample data */
  well_samples: ExtractedWellSample[];

  /** Primary contaminant for the site (matches plume map or highest MCL exceedance) */
  primary_contaminant: string;

  /** Extended metadata including lab report date */
  metadata?: {
    contour_interval_ft?: number;
    reference_datum?: string;
    map_scale?: string;
    map_date?: string;
    /** Date of lab report if different from map date */
    lab_report_date?: string;
  };
}

// ============================================================================
// WELL CONCENTRATION PREDICTIONS (from /concentration routes)
// ============================================================================

/**
 * Single well+analyte concentration prediction.
 */
export interface WellPrediction {
  id: string;
  well_id: string;
  well_name: string;
  analyte_code: string;
  analyte_group: string;
  prediction_date: string;
  target_date: string;
  prediction_horizon_days: number;
  current_conc_mgl: number | null;
  predicted_conc_mgl: number;
  predicted_delta_log10: number;
  predicted_log10: number;
  mcl_mgl: number;
  exceedance_probability: number;
  current_exceeds_mcl: boolean | null;
  ci_90_lower_mgl: number | null;
  ci_90_upper_mgl: number | null;
  ci_90_lower_log10: number | null;
  ci_90_upper_log10: number | null;
  trend: string;
  trend_confidence: string;
  model_version: string;
  model_type: string;
}

/**
 * Response from well predictions endpoint.
 */
export interface WellPredictionsResponse {
  site_id: string;
  predictions: WellPrediction[];
  total: number;
  page: number;
  page_size: number;
  pages: number;
  analytes: string[];
}

/**
 * Single well+analyte prediction at one v3.2 calendar horizon.
 *
 * `current_*` fields carry the most recent measured analytical sample for the
 * (well, analyte) pair so the horizon table can show a Current column next to
 * the forecast horizons without an additional round-trip.
 */
export interface WellPredictionByMonth {
  well_id: string;
  well_name: string;
  analyte_code: string;
  analyte_group: string;
  target_date: string;
  predicted_conc_mgl: number;
  predicted_log10: number;
  ci_90_lower_mgl?: number | null;
  ci_90_upper_mgl?: number | null;
  mcl_mgl: number;
  exceedance_probability: number;
  current_exceeds_mcl: boolean | null;
  trend: string;
  trend_confidence: string;
  current_conc_mgl?: number | null;
  current_sample_date?: string | null;
  current_is_non_detect?: boolean | null;
}

/**
 * All predictions for a single v3.2 calendar horizon.
 * `month` is months-from-now (3/12/24/60/120/240) and is preserved for
 * legacy slider use. `horizon_label` is the v3.2 horizon name.
 */
export interface MonthSlice {
  month: number;
  horizon_label?: string | null;
  horizon_days?: number | null;
  predictions: WellPredictionByMonth[];
}

/**
 * Timeline predictions response for slider integration.
 */
export interface TimelinePredictionsResponse {
  site_id: string;
  months: MonthSlice[];
  analytes: string[];
}

/**
 * Site-level concentration summary with closure timeline.
 */
export interface SiteConcentrationSummary {
  id: string;
  site_id: string;
  prediction_date: string;
  risk_score: number;
  risk_category: string;
  max_exceedance_probability: number;
  p75_exceedance_probability: number | null;
  avg_exceedance_probability: number;
  wells_exceeding_mcl_likely: number;
  total_wells_assessed: number;
  site_trend: string;
  improving_wells_pct: number | null;
  closure_median_quarters: number | null;
  closure_p10_quarters: number | null;
  closure_p90_quarters: number | null;
  closure_probability_10yr: number | null;
  closure_confidence: string | null;
  bottleneck_well_id: string | null;
  bottleneck_well_name: string | null;
  bottleneck_analyte: string | null;
  closure_trajectory: { quarter: number; max_conc_mgl: number; wells_exceeding: number }[] | null;
  model_version: string;
}

// ============================================================================
// HISTORICAL TIMELINE TYPES (Time-Travel Visualization)
// ============================================================================


// ============================================================================

/**
 * A single event in the site timeline.
 */
export interface HistoricalEvent {
  /** Date of the event (ISO format) */
  date: string;
  /** Type of data available: 'sampling', 'gauging', 'both', or 'prediction' */
  type: 'sampling' | 'gauging' | 'both' | 'prediction';
  /** Number of wells with data */
  well_count: number;
  /** Number of compounds sampled (sampling events only) */
  compound_count: number;
  /** Whether gauging data is available */
  has_gauging: boolean;
  /** Whether sampling data is available */
  has_sampling: boolean;
  /** Whether this is a future ML prediction */
  is_prediction?: boolean;
}

/**
 * Response from timeline endpoint.
 */
export interface TimelineResponse {
  site_id: string;
  site_name: string;
  /** Today's date (ISO format) */
  today?: string;
  /** Chronological list of events */
  events: HistoricalEvent[];
  /** Date range of available data */
  date_range: {
    start: string;
    end: string;
  };
  /** Total number of sampling events */
  total_sampling_events: number;
  /** Total number of gauging events */
  total_gauging_events: number;
  /** Total number of prediction events */
  total_prediction_events?: number;
  /** List of all detected compounds */
  unique_compounds: string[];
}

/**
 * Well concentration at a point in time.
 */
export interface HistoricalWellConcentration {
  well_id: string;
  lat: number;
  lon: number;
  compound: string;
  concentration_ug_l: number;
  is_non_detect: boolean;
  mcl_ug_l: number | null;
  exceeds_mcl: boolean;
}

/**
 * Well water level at a point in time.
 */
export interface HistoricalWellWaterLevel {
  well_id: string;
  lat: number;
  lon: number;
  water_elevation_ft: number | null;
  depth_to_water_ft: number | null;
  product_thickness_ft: number | null;
}

/**
 * Gradient from historical water levels.
 */
export interface HistoricalGradient {
  magnitude_ft_ft: number;
  direction_deg: number;
  num_wells_used: number;
  avg_water_level_ft: number;
  water_level_range_ft: number;
}

/**
 * Compound boundary data from historical plume state.
 * Same structure as ContaminantBoundaryData for consistent rendering.
 */
export interface HistoricalCompoundBoundary {
  compound: string;
  boundary: [number, number][];
  wells_with_detections: WellDetection[];
  wells_below_threshold: WellDetection[];
  max_concentration_ug_l: number;
  mcl_ug_l: number;
  threshold_ug_l: number;
  well_count: number;
}

/**
 * Response from historical plume state endpoint.
 */
export interface HistoricalPlumeStateResponse {
  /** Date of the snapshot (ISO format) */
  snapshot_date: string;
  /** Gradient calculated from water levels */
  gradient: HistoricalGradient | null;
  /** Groundwater flow direction */
  flow_direction_deg: number;
  /** Plume boundaries per compound */
  compound_boundaries: HistoricalCompoundBoundary[];
  /** All well concentrations at this time */
  well_concentrations: HistoricalWellConcentration[];
  /** All water levels at this time */
  well_water_levels: HistoricalWellWaterLevel[];
  /** Summary statistics */
  summary: {
    compounds_detected: string[];
    wells_with_data: number;
    max_concentration_ug_l: number;
    max_concentration_compound: string;
  };
  /** Data source: 'historical' for past/today, 'prediction' for future */
  data_source?: 'historical' | 'prediction';
  /** Pre-computed PSH wells with interior inference (snapshot v2+) */
  psh_wells?: Array<{
    well_id: string;
    lat: number;
    lon: number;
    product_thickness_ft: number;
    depth_to_water_ft?: number;
    is_inferred?: boolean;
  }>;
}

/**
 * Response from plume evolution endpoint.
 */
export interface PlumeEvolutionResponse {
  site_id: string;
  site_name: string;
  date_range: {
    start: string | null;
    end: string | null;
  };
  total_events_in_range: number;
  snapshots_returned: number;
  /** Array of plume states across time */
  snapshots: HistoricalPlumeStateResponse[];
}

/**
 * Pre-computed quarterly plume snapshot with quarter label.
 */
export interface QuarterlyPlumeSnapshot extends HistoricalPlumeStateResponse {
  /** Quarter label: "2020-Q1", "2020-Q2", etc. */
  quarter: string;
  /** Quarter-end date used for computation (ISO format) */
  date: string;
}

/**
 * Response from bulk plume snapshots endpoint.
 * Returns all pre-computed quarterly snapshots for instant timeline scrubbing.
 */
export interface PlumeSnapshotsResponse {
  site_id: string;
  snapshots: QuarterlyPlumeSnapshot[];
  count: number;
  /** Whether snapshots have been pre-computed. If false, frontend falls back to per-date fetching. */
  precomputed: boolean;
}

/**
 * Fetch all pre-computed quarterly plume snapshots for a site.
 * Returns all snapshots in one request for instant timeline scrubbing.
 */
export async function fetchPlumeSnapshots(siteId: string): Promise<PlumeSnapshotsResponse> {
  const resp = await apiClient.get<PlumeSnapshotsResponse>(
    `/polygon/physics/sites/${siteId}/plume-snapshots`
  );
  return resp.data;
}

/**
 * Response from predictive (future) plume snapshots endpoint.
 * Same shape as PlumeSnapshotsResponse — frontend renders identically.
 * When slider crosses "Today", switches from historical to predictive map.
 */
export interface PredictiveSnapshotsResponse {
  site_id: string;
  snapshots: QuarterlyPlumeSnapshot[];
  count: number;
  precomputed: boolean;
}

/**
 * Fetch all pre-computed FUTURE quarterly plume snapshots for a site.
 * Returns predictive snapshots based on ML concentration predictions.
 */
export async function fetchPredictiveSnapshots(siteId: string): Promise<PredictiveSnapshotsResponse> {
  const resp = await apiClient.get<PredictiveSnapshotsResponse>(
    `/polygon/physics/sites/${siteId}/predictive-snapshots`
  );
  return resp.data;
}

// ============================================================================
// SENTINEL ALERTS — DW (Delineation Well) Recommendation Types
// ============================================================================

export interface DWRecommendation {
  recommended_lat: number;
  recommended_lon: number;
  bearing_deg: number;
  distance_from_source_ft: number;
  recommendation_distance_ft: number;
  trigger_well_id: string;
  trigger_well_name: string;
  trigger_compounds: string[];
  priority: string;
  rationale: string;
  is_predicted: boolean;
  projected_concentration_ugl: number;
  /** Trigger well's current MCL exceedance ratio (e.g. 5.2 = 5.2× over MCL). */
  trigger_current_exceedance_ratio?: number;
  /** ML-predicted probability the trigger well will breach (further) in the
   *  prediction window. 0–1. */
  trigger_predicted_exceedance_probability?: number;
  /** Composite severity = current_ratio × (1 + 2 × predicted_probability).
   *  Use as a within-priority sort key — higher = worse now AND projected
   *  to get worse, so install a DW here first. */
  severity_score?: number;
  /** Ranking signals.
   *    flow_alignment_score:        1.0 perfectly downgradient, 0.0 upgradient.
   *    relative_exceedance_rank:    trigger well's MCL exceedance percentile vs other breached wells at the site (0–1).
   *    combined_rank:               0.5 × flow_alignment + 0.5 × relative_exceedance_rank.
   *                                 Single canonical sort key. */
  flow_alignment_score?: number;
  relative_exceedance_rank?: number;
  combined_rank?: number;
}

export interface RemediationHotspot {
  well_id: string;
  well_name: string;
  lat: number;
  lon: number;
  types: ('psh' | 'dissolved')[];
  psh_thickness_ft: number | null;
  psh_site_median_ft: number | null;
  dissolved_max_conc_ugl: number | null;
  dissolved_compound: string | null;
  dissolved_mcl_ugl: number | null;
  dissolved_site_median_ugl: number | null;
  severity: 'critical' | 'high' | 'moderate';
  rationale: string;
}

export interface SentinelAlertsResponse {
  site_id: string;
  site_name: string;
  analysis_date: string;
  source_lat: number;
  source_lon: number;
  flow_direction_deg: number | null;
  total_wells: number;
  recommendations: DWRecommendation[];
  remediation_hotspots: RemediationHotspot[];
  coverage_score: number;
  insufficient_network: boolean;
  sentinel_wells: any[];
  at_limit_wells: any[];
  predicted_limit_wells: any[];
}

// ============================================================================
// COST PROJECTION SNAPSHOTS — Pre-computed cost projections
// ============================================================================

export interface CostProjectionMonthly {
  month: number;
  total_cost: number;
  contingency: number;
  target_date: string;
  wells_breached: number;
  monitoring_cost: number;
  regulatory_cost: number;
  perimeter_well_cost: number;
  base_remediation_cost: number;
  perimeter_wells_needed: number;
  well_installation_cost: number;
  cost_increase_percentage: number;
  cost_increase_from_baseline: number;
}

export interface SentinelWellCost {
  lat: number;
  lon: number;
  priority: string;
  depth_tier: string;
  avg_depth_ft?: number;
  install_cost: number;
  trigger_well: string;
  rationale: string;
}

export interface CostProjectionSnapshotResponse {
  precomputed: boolean;
  site_id: string;
  site_name: string;
  schema_version?: number;
  stale?: boolean;
  threat_tier?: string | null;
  current_cost?: number | null;
  cost_6mo?: number | null;
  cost_12mo?: number | null;
  cost_24mo?: number | null;
  monthly_burn_rate?: number | null;
  well_count?: number | null;
  computed_at?: string | null;
  computation_ms?: number | null;
  projection?: {
    baseline_cost: number;
    monthly_projections: CostProjectionMonthly[];
    threat_tier: number;
    threat_name: string;
    burn_rate: number;
    cost_doubles_at_month: number | null;
    sentinel_wells: SentinelWellCost[];
    activity_costs_used: Record<string, number>;
    cost_buckets?: CostBuckets;
  } | null;
  message?: string;
}

export interface CostProjectionBatchItem {
  site_id: string;
  site_name: string;
  precomputed: boolean;
  threat_tier: string | null;
  current_cost: number | null;
  cost_6mo: number | null;
  cost_12mo: number | null;
  cost_24mo: number | null;
  monthly_burn_rate: number | null;
  well_count: number | null;
  computed_at: string | null;
}

export interface CostProjectionBatchResponse {
  total_sites: number;
  sites_with_projections: number;
  snapshots: CostProjectionBatchItem[];
}

/**
 * Fetch pre-computed cost projection for a single site.
 * Returns the full projection_json or a stub if not yet computed.
 */
export async function fetchCostProjectionSnapshot(siteId: string): Promise<CostProjectionSnapshotResponse> {
  const resp = await apiClient.get<CostProjectionSnapshotResponse>(
    `/polygon/sites/${siteId}/cost-projection-snapshot`
  );
  return resp.data;
}

/**
 * Fetch pre-computed cost projections for all sites (portfolio batch).
 */
export async function fetchCostProjectionSnapshots(): Promise<CostProjectionBatchResponse> {
  const resp = await apiClient.get<CostProjectionBatchResponse>(
    `/polygon/cost-projection-snapshots`
  );
  return resp.data;
}

// ============================================================================
// ACTIVITY COST SETTINGS — CRUD for client cost overrides
// ============================================================================

export interface ActivityCostItem {
  activity_code: string;
  display_name: string;
  category: string;
  unit: string;
  default_cost: number;
  min_cost: number | null;
  max_cost: number | null;
  is_override: boolean;
  notes: string | null;
}

export interface ActivityCostListResponse {
  activities: ActivityCostItem[];
}

export async function fetchActivityCosts(): Promise<ActivityCostListResponse> {
  const resp = await apiClient.get<ActivityCostListResponse>('/polygon/cost-settings/activities');
  return resp.data;
}

// ============================================================================
// v3.2 Cost Curve — siteCost helper consumed by 4 dashboard cards + portfolio
// ============================================================================

export interface V32CostCurvePoint {
  label: string;
  days: number;
  years: number;
  wells_hot: number;
  monitoring_cost: number;
  treatment_cost: number;
  delineation_install_cost: number;
  delineation_monitor_cost: number;
  total_cost: number;
}

export interface V32WellSummary {
  well_id: string;
  well_name: string | null;
  worst_ratio: number;
  worst_tier_key: string;
  worst_tier_label: string;
  worst_tier_cost_per_yr: number;
  clear_year: number;
  exceeds_at_20yr: boolean;
  worst_horizon_p_exceed: number;
}

export interface V32GroupCurvePoint {
  label: string;
  days: number;
  years: number;
  wells_hot: number;
  total_cost: number;
}

export interface V32CostCurveResponse {
  site_id: string;
  site_name: string;
  library_slug: string | null;
  existing_well_count: number;
  num_delineation_wells: number;
  curve: V32CostCurvePoint[];
  // Per-group curves used by the dashboard sparklines. BTEX runs 7 points
  // (now → 20yr); TPH caps at 4 points (now → 2yr). Empty when the site has
  // no predictions for that group.
  btex_curve: V32GroupCurvePoint[];
  tph_curve: V32GroupCurvePoint[];
  // Per-group analyte ratio trajectories — avg conc/MCL across all wells at
  // each future horizon. BTEX reaches 20yr, TPH reaches 2yr.
  btex_trajectory: { horizons: string[]; ratios: (number | null)[] };
  tph_trajectory: { horizons: string[]; ratios: (number | null)[] };
  wells: V32WellSummary[];
  // BTEX-dominant sites extend to 20yr; TPH-dominant cap at 2yr because
  // long-horizon TPH bundles are do_not_use in v3.2.
  dominant_group: 'BTEX' | 'TPH';
  delay_horizon_days: number;
  delay_horizon_label: string;       // "5yr" (BTEX) or "1yr" (TPH)
  liability_horizon_days: number;
  liability_horizon_label: string;   // "20yr" (BTEX) or "2yr" (TPH)
  cards: {
    start_now: number;
    cost_of_delay: number;
    liability: number;
    delineation_cost: number;
  };
  hotspot_overlay: Record<string, {
    well_name: string | null;
    worst_ratio: number;
    worst_tier_label: string;
    clear_year: number;
    exceeds_at_max_horizon: boolean;
    worst_horizon_p_exceed: number;
    remediate_now: boolean;
  }>;
}

export async function fetchV32CostCurve(
  siteId: string,
  librarySlug?: string | null,
): Promise<V32CostCurveResponse> {
  const params = librarySlug ? { library_slug: librarySlug } : undefined;
  const resp = await apiClient.get<V32CostCurveResponse>(
    `/polygon/sites/${siteId}/v32-cost-curve`,
    { params },
  );
  return resp.data;
}

export interface V32PortfolioCostSummary {
  as_of: string;
  library_slug: string | null;
  total_sites: number;
  total_start_now: number;
  total_cost_of_delay: number;
  total_liability: number;
  avg_cost_of_delay: number;
  sites: Array<{
    site_id: string;
    site_name: string;
    dominant_group: 'BTEX' | 'TPH';
    delay_horizon_label: string;
    liability_horizon_label: string;
    start_now: number;
    cost_of_delay: number;
    liability: number;
    delineation_cost: number;
    wells_exceeding_at_max_horizon: number;
    cost_sparkline: number[];
    cost_sparkline_labels: string[];
    // Per-group analyte ratio trajectories. BTEX horizons reach 20yr, TPH
    // reach 2yr — each goes to that group's native max horizon.
    btex_trajectory: { horizons: string[]; ratios: (number | null)[] };
    tph_trajectory: { horizons: string[]; ratios: (number | null)[] };
  }>;
}

export async function fetchV32PortfolioCostSummary(
  librarySlug?: string | null,
): Promise<V32PortfolioCostSummary> {
  const params = librarySlug ? { library_slug: librarySlug } : undefined;
  const resp = await apiClient.get<V32PortfolioCostSummary>(
    `/polygon/portfolio/v32-cost-summary`,
    { params },
  );
  return resp.data;
}

export async function updateActivityCost(activityCode: string, defaultCost: number): Promise<void> {
  await apiClient.put(`/polygon/cost-settings/activities/${activityCode}`, { default_cost: defaultCost });
}

export async function resetActivityCost(activityCode: string): Promise<void> {
  await apiClient.delete(`/polygon/cost-settings/activities/${activityCode}`);
}


// ============================================================================
// REMEDIATION SYSTEMS — CRUD for site remediation systems
// ============================================================================

export interface RemediationSystemItem {
  id: string;
  site_id: string;
  technology: string;
  status: string;
  install_date: string | null;
  shutdown_date: string | null;
  capital_cost: number | null;
  monthly_operating_cost: number | null;
  extra_data: Record<string, any> | null;
  source: string; // "user_declared" | "recommended"
}

export interface RemediationSystemListResponse {
  site_id: string;
  systems: RemediationSystemItem[];
}

export async function fetchRemediationSystems(siteId: string): Promise<RemediationSystemListResponse> {
  const resp = await apiClient.get<RemediationSystemListResponse>(`/polygon/sites/${siteId}/remediation-systems`);
  return resp.data;
}

export async function createRemediationSystem(
  siteId: string,
  data: {
    technology: string;
    status?: string;
    install_date?: string;
    capital_cost?: number;
    monthly_operating_cost?: number;
    extra_data?: Record<string, any>;
  },
): Promise<any> {
  const resp = await apiClient.post(`/polygon/sites/${siteId}/remediation-systems`, data);
  return resp.data;
}

export async function updateRemediationSystem(
  systemId: string,
  data: Partial<RemediationSystemItem>,
): Promise<any> {
  const resp = await apiClient.put(`/polygon/remediation-systems/${systemId}`, data);
  return resp.data;
}

export async function deleteRemediationSystem(systemId: string): Promise<void> {
  await apiClient.delete(`/polygon/remediation-systems/${systemId}`);
}


// ============================================================================
// REMEDIATION IMPACT CALCULATOR — feeds the calculator card under the MCL legend
// ============================================================================

export interface RemediationDraftSystem {
  technology: string;
  install_date?: string;          // YYYY-MM-DD
  shutdown_date?: string;
  design_flow_gpm?: number | null;
  lat?: number | null;
  lon?: number | null;
  roi_ft?: number | null;
}

export interface RemediationSystemImpact {
  system_id: string;
  technology: string;
  is_draft: boolean;
  roi_ft: number;
  wells_in_roi: number;
  removed_psh_lbs_by_horizon: Record<string, number>;
  removed_dissolved_mg_by_horizon: Record<string, number>;
}

export interface RemediationImpactResponse {
  site_id: string;
  horizons_days: number[];
  horizons_labels: string[];
  baseline_psh_lbs: number[];
  adjusted_psh_lbs: number[];
  baseline_dissolved_mg: number[];
  adjusted_dissolved_mg: number[];
  per_system: RemediationSystemImpact[];
  /** Cost-savings comparison: same v3.2 cost engine run with vs without the
   *  declared remediation systems. Null if either run failed. */
  cost_savings?: {
    start_now_with: number;
    start_now_without: number;
    liability_20yr_with: number;
    liability_20yr_without: number;
    savings_start_now: number;
    savings_20yr: number;
  } | null;
}

// ============================================================================
// DW name overrides — let users rename DW recommendations site-by-site
// ============================================================================

export async function fetchDwNameOverrides(siteId: string): Promise<Record<string, string>> {
  const resp = await apiClient.get<{ overrides: Record<string, string> }>(
    `/polygon/sites/${siteId}/dw-names`,
  );
  return resp.data.overrides ?? {};
}

export async function setDwNameOverride(
  siteId: string,
  proposedWellId: string,
  customName: string | null,
): Promise<Record<string, string>> {
  const resp = await apiClient.put<{ overrides: Record<string, string> }>(
    `/polygon/sites/${siteId}/dw-names`,
    { proposed_well_id: proposedWellId, custom_name: customName },
  );
  return resp.data.overrides ?? {};
}


export async function fetchRemediationImpact(
  siteId: string,
  opts?: {
    drafts?: RemediationDraftSystem[];
    baselinePshLbs?: number;
    baselineDissolvedMg?: number;
  },
): Promise<RemediationImpactResponse> {
  const params: Record<string, number> = {};
  if (opts?.baselinePshLbs != null) params.baseline_psh_lbs = opts.baselinePshLbs;
  if (opts?.baselineDissolvedMg != null) params.baseline_dissolved_mg = opts.baselineDissolvedMg;
  const resp = await apiClient.post<RemediationImpactResponse>(
    `/polygon/sites/${siteId}/remediation-impact`,
    opts?.drafts ?? [],
    { params },
  );
  return resp.data;
}


// ============================================================================
// THREE COST BUCKETS — Real-time cost breakdown
// ============================================================================

export interface CostBucketSentinelWell {
  lat: number;
  lon: number;
  priority: string;
  depth_tier: string;
  avg_depth_ft?: number;
  install_cost: number;
  trigger_well: string;
  rationale: string;
}

export interface MdpeBreakdown {
  equipment: { events: number; rate: number; duration: string; total: number };
  tedlar: { per_event: number; rate: number; total: number };
  per_diem: { rate: number; total: number; included: boolean };
  fuel: { rate: number; total: number; included: boolean };
  lab: { monthly: number };
  total_monthly: number;
}

export interface MdpeConfig {
  event_duration: '12hr' | '24hr' | 'monthly';
  events_per_month: number;
  tedlar_bags_per_event: number;
  include_per_diem: boolean;
  include_fuel: boolean;
}

export interface CostBucketRemediationSystem {
  system_id: string;
  technology: string;
  source: string;
  monthly_cost: number;
  annual_cost: number;
  install_date: string | null;
  mdpe_breakdown?: MdpeBreakdown;
}

export interface CostBucketHotspot {
  well_id: string;
  well_name: string;
  lat: number;
  lon: number;
  types: string[];
  psh_thickness_ft: number | null;
  dissolved_max_conc_ugl: number | null;
  dissolved_compound: string | null;
  severity: string;
  rationale: string;
}

export interface CostBuckets {
  new_well_installation: {
    label: string;
    sentinel_wells: CostBucketSentinelWell[];
    cost_per_well: number;
    depth_tier: string;
    avg_depth_ft?: number;
    total: number;
    type: string;
  };
  active_remediation: {
    label: string;
    systems: CostBucketRemediationSystem[];
    monthly_total: number;
    annual_total: number;
    type: string;
  };
  om_monitoring: {
    label: string;
    well_count: number;
    depth_tier: string;
    avg_depth_ft?: number;
    quarterly_sampling_cost: number;
    quarterly_report_cost: number;
    quarterly_mobilization: number;
    quarterly_total: number;
    annual_total: number;
    breakdown: {
      sampling_per_well: number;
      lab_per_well: number;
      quarterly_report: number;
      annual_report: number;
      mobilization: number;
    };
    type: string;
  };
  recommended_remediation?: {
    label: string;
    hotspots: CostBucketHotspot[];
    type: string;
  };
  grand_total_annual: number;
  grand_total_capital: number;
}

export async function fetchCostBuckets(siteId: string, fresh = false): Promise<CostBuckets> {
  const resp = await apiClient.get<CostBuckets>(`/polygon/sites/${siteId}/cost-buckets`, {
    params: fresh ? { fresh: true } : undefined,
  });
  return resp.data;
}

// =============================================================================
// CAPITAL ALLOCATION (Terra-driven portfolio recommender)
// =============================================================================

export interface CapitalRecommendationRequest {
  total_budget: number;
  fiscal_year?: number;
  max_sites?: number;
  min_allocation?: number;
}

export interface CapitalAllocationDrivers {
  dw_install_cost: number;
  remediation_24mo_cost: number;
  plume_expansion_cost: number;
  /** Concentration growth: decimal % change in max measured conc, last 12mo
   *  vs prior 12mo. 0.25 = +25% (rising → urgent), -0.10 = -10% (improving). */
  concentration_growth_pct: number;
}

export interface CapitalAllocationFunded {
  site_id: string;
  site_name: string;
  amount: number;
  rationale: string;
  drivers: CapitalAllocationDrivers;
}

export interface CapitalAllocationDeferred {
  site_id: string;
  site_name: string;
  reason: string;
  drivers: CapitalAllocationDrivers;
}

export interface CapitalRecommendationResponse {
  fiscal_year: number | null;
  total_budget: number;
  total_allocated: number;
  unallocated: number;
  funded: CapitalAllocationFunded[];
  deferred: CapitalAllocationDeferred[];
  notes: string;
}

// =============================================================================
// REGULATORY LIBRARY DROPDOWN (Data Vault → plume + DW cascade)
// =============================================================================

export interface RegLibrary {
  slug: string;
  display_name: string;
  authority: string;
  active_limit_count: number;
}

export interface RegLibrariesResponse {
  libraries: RegLibrary[];
  default_slug: string;
}

export interface RegLibraryKeyMcl {
  display: string;
  analyte_code: string;
  mcl_mgl: number | null;
  fallback_to_epa_mcl: boolean;
}

export interface RegLibraryKeyMclsResponse {
  slug: string;
  compounds: RegLibraryKeyMcl[];
}

/** One field on the /observed-metrics response. value=null means insufficient
 *  data; the UI falls back to the three-plume simulation in that case. */
export interface ObservedField<T> {
  value: T | null;
  as_of_date: string | null;
  source_note: string | null;
}

export interface ObservedMetricsResponse {
  site_id: string;
  library_slug: string | null;
  lead_contaminant: ObservedField<string>;
  initial_psh_lbs: ObservedField<number>;
  psh_depleted_pct: ObservedField<number>;
  peak_dissolved_sqft: ObservedField<number>;
  remediation: ObservedField<string>;
}

export default polygonApi;
